import { Icon, Tooltip, token } from "@may-i/mash-design-system";
import {
  Canvas,
  Circle,
  FabricImage,
  FabricObject,
  FabricText,
  Group,
  Line,
  Point,
  Polygon,
  Polyline,
  Rect,
  Shadow,
  Triangle,
  util,
} from "fabric";
import type { TMat2D } from "fabric";
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import type {
  CountingLine,
  CountingLineEventType,
  IntervalZone,
  IntervalZoneEventType,
} from "~/entities/analysis-config/types";
import type { CameraMemo } from "~/entities/camera-memo/types";
import { MemoMarker } from "~/pages/camera-mapping/fabric-objects/MemoMarker";
import { IcEraser } from "~/shared/assets/icons/IcEraser";

// --- Constants ---

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const FIT_MARGIN = 0.94;

/**
 * 배경 이미지를 캔버스 가용 영역(상단 overlay/하단 ActionBar 제외)에 contain-fit.
 * PO QA 2차 o: viewMode/overlay 변화 시 dynamic refit 용 헬퍼.
 * - verticalAlign "center" (단독 보기): 가용 영역 안에서 0.8 마진 중앙 배치.
 * - verticalAlign "bottom" (둘 다 모드 위쪽): 이미지 아래쪽이 (canvasH - bottomPadding) 에 anchor — 아래 캔버스와 갭 제거.
 */
type VerticalAlign = "center" | "top" | "bottom";

/**
 * bg refit 후 캔버스 위의 모든 비-배경 오브젝트 좌표를 동기 변환.
 * React effect 를 기다리지 않고 즉시 처리해 리사이즈·topPadding 변경 중 깜빡임 방지.
 */
function repositionObjectsAfterBgRefit(
  canvas: Canvas,
  ns: { width: number; height: number },
  oldLeft: number,
  oldTop: number,
  oldScale: number,
  bgFlag: string,
) {
  const bgImg = canvas
    .getObjects()
    .find((o) => (o as any)[bgFlag] === true) as FabricImage | undefined;
  if (!bgImg) return;
  const newLeft = bgImg.left!;
  const newTop = bgImg.top!;
  const newScale = bgImg.scaleX!;
  if (newLeft === oldLeft && newTop === oldTop && newScale === oldScale) return;

  const oldTlX = oldLeft - (ns.width * oldScale) / 2;
  const oldTlY = oldTop - (ns.height * oldScale) / 2;
  const newTlX = newLeft - (ns.width * newScale) / 2;
  const newTlY = newTop - (ns.height * newScale) / 2;
  const ratio = oldScale > 0 ? newScale / oldScale : 1;

  for (const obj of canvas.getObjects()) {
    if ((obj as any)[bgFlag]) continue;
    obj.set({
      left: (obj.left! - oldTlX) * ratio + newTlX,
      top: (obj.top! - oldTlY) * ratio + newTlY,
      scaleX: obj.scaleX! * ratio,
      scaleY: obj.scaleY! * ratio,
    });
    obj.setCoords();
  }
}

function fitBgToCanvas(
  canvas: Canvas,
  bgImg: FabricImage,
  naturalSize: { width: number; height: number },
  topPadding: number,
  bottomPadding: number,
  verticalAlign: VerticalAlign = "center",
) {
  const canvasW = canvas.getWidth();
  const canvasH = canvas.getHeight();
  const effectiveH = Math.max(canvasH - topPadding - bottomPadding, 1);
  let scale: number;
  if (verticalAlign === "center") {
    // 유효 높이의 80%를 목표로 하되 가로 오버플로우 방지
    const targetScale = (effectiveH * FIT_MARGIN) / naturalSize.height;
    scale = Math.min(targetScale, canvasW / naturalSize.width);
  } else {
    scale = Math.min(canvasW / naturalSize.width, effectiveH / naturalSize.height);
  }
  const scaledH = naturalSize.height * scale;
  let centerY: number;
  if (verticalAlign === "top") {
    centerY = topPadding + scaledH / 2;
  } else if (verticalAlign === "bottom") {
    centerY = canvasH - bottomPadding - scaledH / 2;
  } else {
    centerY = topPadding + effectiveH / 2;
  }
  bgImg.set({
    scaleX: scale,
    scaleY: scale,
    left: canvasW / 2,
    top: centerY,
  });
  bgImg.setCoords();
}

const LINE_STROKE = "#70FF00";
const LINE_STROKE_WIDTH = 2;

const ZONE_STROKE = "#19E3FF";
const ZONE_FILL = "rgba(25, 227, 255, 0.27)";

const BORDER_COLOR = "#5FB3FF";
const CORNER_COLOR = "white";
const CORNER_STROKE_COLOR = "#329eff";

const ARROW_HEIGHT = 16;
const ARROW_TRI_WIDTH = 12;
const ARROW_TRI_HEIGHT = 8;

const LINE_DIR_LABEL_STYLE = {
  color: "#2C2D2E",
  backgroundColor: "#FFFFFF",
  fontWeight: 500,
  stroke: LINE_STROKE,
  strokeWidth: 1,
  fontSize: 12,
  fontFamily: "Pretendard Variable, Pretendard, sans-serif",
  radius: 1,
  paddingH: 6,
  paddingV: 4,
  shadow: "0 1px 10px rgba(0,0,0,0.3)",
};

const LINE_NAME_LABEL_STYLE = {
  color: "#FFFFFF",
  backgroundColor: "#31B400",
  fontWeight: 600,
  stroke: "#FFFFFF",
  strokeWidth: 1,
  fontSize: 12,
  fontFamily: "Pretendard Variable, Pretendard, sans-serif",
  radius: 1000,
  paddingH: 8,
  paddingV: 4,
  shadow: "0 1px 10px rgba(0,0,0,0.3)",
};

const ZONE_LABEL_STYLE = {
  color: "#FFFFFF",
  backgroundColor: "#0090A3",
  fontWeight: 600,
  stroke: "#FFFFFF",
  strokeWidth: 1,
  fontSize: 12,
  fontFamily: "Pretendard Variable, Pretendard, sans-serif",
  radius: 1000,
  paddingH: 8,
  paddingV: 4,
  shadow: "0 1px 10px rgba(0,0,0,0.3)",
};

// PO QA 3차 ab-4: 라벨/화살표/stroke 같은 fixed-pixel 요소들이 배경 scale 변경 시
// 배경 대비 상대적으로 커 보이는 문제 — visualScale(=현재 bgScale / 카메라 진입 시점 bgScale)
// 을 곱해 비례 축소. 단 가독성 위해 최소 크기 보장.
const MIN_FONT_SIZE = 8;
const MIN_STROKE_WIDTH = 1;
const MIN_ARROW_HEIGHT = 8;
const MIN_ARROW_TRI_W = 6;
const MIN_ARROW_TRI_H = 4;
const MIN_LABEL_PAD_H = 2;
const MIN_LABEL_PAD_V = 2;

function clampScaled(base: number, visualScale: number, min: number): number {
  return Math.max(min, base * visualScale);
}

type LabelStyleInput = typeof LINE_DIR_LABEL_STYLE | typeof LINE_NAME_LABEL_STYLE | typeof ZONE_LABEL_STYLE;

function scaleLabelStyle(style: LabelStyleInput, visualScale: number) {
  const fontSize = clampScaled(style.fontSize, visualScale, MIN_FONT_SIZE);
  // PO QA 8차: 여백을 폰트와 "동일 비율"로 축소. 기존엔 폰트·여백을 각각 독립 MIN 으로 클램프해
  // 폰트가 MIN(8)에 걸려도 여백은 2까지 떨어져 여백/폰트 비율이 깨짐 → 라벨이 좁아 보였음.
  const effScale = fontSize / style.fontSize;
  return {
    ...style,
    fontSize,
    paddingH: Math.max(MIN_LABEL_PAD_H, style.paddingH * effScale),
    paddingV: Math.max(MIN_LABEL_PAD_V, style.paddingV * effScale),
  };
}

function scaledArrowHeight(visualScale: number) {
  return clampScaled(ARROW_HEIGHT, visualScale, MIN_ARROW_HEIGHT);
}
function scaledLineStroke(visualScale: number) {
  return clampScaled(LINE_STROKE_WIDTH, visualScale, MIN_STROKE_WIDTH);
}

import {
  DRAW_VERTEX_RADIUS,
  isNearFirstPoint,
  removePreviewLine,
  createPreviewLine,
  updatePreviewLine,
  updateFirstMarkerOnHover,
  snapToAxis,
} from "~/shared/lib/fabric-zone-drawing";

/** Build an SVG data-URL cursor that looks like the drawing vertex marker. */
function buildDrawingCursor(strokeColor: string): string {
  const r = 4;
  const sw = 1.5;
  const size = (r + sw) * 2;
  const center = size / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${center}" cy="${center}" r="${r}" fill="white" stroke="${strokeColor}" stroke-width="${sw}"/></svg>`;
  const encoded = btoa(svg);
  return `url(data:image/svg+xml;base64,${encoded}) ${center} ${center}, crosshair`;
}

const LINE_DRAWING_CURSOR = buildDrawingCursor(LINE_STROKE);
const ZONE_DRAWING_CURSOR = buildDrawingCursor(ZONE_STROKE);

// F19: Rectangle zone drawing cursor — ic_area_zone icon (rounded square with corner dots)
function buildRectDrawingCursor(fillColor: string): string {
  const size = 22;
  const center = size / 2;
  // ic_area_zone path scaled from 24→22 via viewBox
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
    <path fill-rule="evenodd" clip-rule="evenodd" fill="${fillColor}" d="M2 4.5a2.5 2.5 0 0 0 1.5 2.292v10.416A2.5 2.5 0 1 0 6.792 20.5h10.416a2.5 2.5 0 1 0 3.292-3.292V6.792A2.5 2.5 0 1 0 17.208 3.5H6.792A2.5 2.5 0 0 0 2 4.5Zm16.5 12.708V6.792A2.51 2.51 0 0 1 17.208 5.5H6.792A2.51 2.51 0 0 1 5.5 6.792v10.416A2.51 2.51 0 0 1 6.792 18.5h10.416a2.51 2.51 0 0 1 1.292-1.292Z"/>
  </svg>`;
  return `url(data:image/svg+xml;base64,${btoa(svg)}) ${center} ${center}, crosshair`;
}
const ZONE_RECT_DRAWING_CURSOR = buildRectDrawingCursor(ZONE_STROKE);

type LineLabelDefs = Record<CountingLineEventType, { left: string; right: string }>;

// --- Types ---

export interface DrawingRequest {
  type: "line" | "zone";
  eventType: CountingLineEventType | IntervalZoneEventType;
}

interface SnapshotCanvasProps {
  /** 6차 an: cross-canvas 자동 종료용 — 현재 편집 중인 캔버스의 finishEdit 콜백 ref. 부모(AnalysisDraftPage) 가 보유 */
  coordinateEditingFinishRef: MutableRefObject<(() => void) | null>;
  snapshotUrl: string | undefined;
  cameraId: number;
  countingLines: CountingLine[];
  intervalZones: IntervalZone[];
  drawingRequest: DrawingRequest | null;
  /** 4-3-2: drawingRequest 활성 시 양쪽 캔버스 중 어디서 그리기 시작했는지 (ownership). null=아직 시작 안 됨 */
  drawingActiveCanvas?: "snapshot" | "floorPlan" | null;
  /** 4-3-2: 첫 클릭 시 ownership 잡기 */
  onDrawingClaim?: (canvas: "snapshot" | "floorPlan") => void;
  /** 4-3-2 역방향: 도면에만 그려진 기존 라인/존을 스냅샷에도 그리기 위한 target. 설정되면 banner 표시 + 그리기 모드 활성. */
  drawingTarget?: { type: "line" | "zone"; id: number; name: string } | null;
  /** drawingTarget 으로 시작한 그리기 완료 시 호출 — 해당 라인/존의 analysisConfig.coordinates 업데이트 */
  onDrawingTargetComplete?: (type: "line" | "zone", id: number, coordinates: { x: number; y: number }[]) => void;
  onDrawingTargetCancel?: () => void;
  onDrawingCancel?: () => void;
  selectedEventSource:
    | { type: "line"; id: number }
    | { type: "zone"; id: number }
    | null;
  onDrawingComplete: (
    type: "line" | "zone",
    eventType: string,
    coordinates: { x: number; y: number }[],
  ) => void;
  onLineCoordinatesChange: (
    lineId: number,
    coordinates: { x: number; y: number }[],
  ) => void;
  onZoneCoordinatesChange: (
    zoneId: number,
    coordinates: { x: number; y: number }[],
  ) => void;
  onSelectionChange: (
    selection:
      | { type: "line"; id: number }
      | { type: "zone"; id: number }
      | null,
  ) => void;
  onRequestDelete?: (type: "line" | "zone", id: number) => void;
  onAddMemo?: () => void;
  showLineZone: boolean;
  zoomButtonsBottomOffset?: number;
  memosOnSnapshot?: CameraMemo[];
  memoPlacingMode?: { memoId: number; cameraId: number } | null;
  selectedMemoId?: number | null;
  onMemoPlaced?: (memoId: number, position: { x: number; y: number }) => void;
  onMemoMarkerClick?: (memoId: number | null) => void;
  onMemoDragEnd?: (memoId: number, position: { x: number; y: number }) => void;
  onDeleteMemoFromSnapshot?: (memoId: number) => void;
  /** PO QA 2차 o: TopOverlay 가 캔버스 상단을 덮는 경우 가용 영역 계산용 padding (px) */
  topPadding?: number;
  /** PO QA 2차 o 후속4: DraftActionBar 가 캔버스 하단을 덮는 경우 가용 영역 계산용 padding (px) */
  bottomPadding?: number;
  /** PO QA 2차 o 후속: 둘 다 모드 위쪽 캔버스는 "bottom" 으로 anchor 해 도면 캔버스와의 갭 제거. 기본 "center". */
  verticalAlign?: "center" | "top" | "bottom";
}

// --- Helpers ---

interface ImageNaturalSize {
  width: number;
  height: number;
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function getNormalizedPoint(p: { x: number; y: number }) {
  return { x: clamp(p.x, 0, 100), y: clamp(p.y, 0, 100) };
}

/**
 * Clamp a pixel-space point so it stays within the snapshot image bounds.
 */
function clampToImageBounds(
  p: { x: number; y: number },
  bgObj: FabricImage,
  naturalSize: ImageNaturalSize,
): { x: number; y: number } {
  const scale = bgObj.scaleX!;
  const scaledW = naturalSize.width * scale;
  const scaledH = naturalSize.height * scale;
  const tlX = bgObj.left! - scaledW / 2;
  const tlY = bgObj.top! - scaledH / 2;
  return {
    x: clamp(p.x, tlX, tlX + scaledW),
    y: clamp(p.y, tlY, tlY + scaledH),
  };
}

function createArrowGroup(
  px: number,
  py: number,
  angleDeg: number,
  stroke: string,
  visualScale: number = 1,
): Group {
  const ah = scaledArrowHeight(visualScale);
  const tw = clampScaled(ARROW_TRI_WIDTH, visualScale, MIN_ARROW_TRI_W);
  const th = clampScaled(ARROW_TRI_HEIGHT, visualScale, MIN_ARROW_TRI_H);
  const sw = scaledLineStroke(visualScale);

  const stick = new Line([0, -1, 0, ah], {
    originX: "center",
    originY: "top",
    stroke,
    strokeWidth: sw,
  });

  const tri = new Triangle({
    width: tw,
    height: th,
    fill: stroke,
    originX: "center",
    originY: "bottom",
    left: 0,
    top: ah - 0.5,
    angle: 180,
  });

  return new Group([stick, tri], {
    left: px,
    top: py,
    originX: "center",
    originY: "top",
    angle: angleDeg,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

function createLabelGroup(
  text: string,
  px: number,
  py: number,
  options: {
    originX?: string;
    originY?: string;
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: number;
    color?: string;
    backgroundColor?: string;
    stroke?: string;
    strokeWidth?: number;
    radius?: number;
    paddingH?: number;
    paddingV?: number;
    shadow?: string;
  } = {},
): Group {
  const fontSize = options.fontSize ?? 12;
  const fontFamily =
    options.fontFamily ?? "Pretendard Variable, Pretendard, sans-serif";
  const fw = options.fontWeight ?? 600;
  const color = options.color ?? "#2C2D2E";
  const bgColor = options.backgroundColor ?? "#FFFFFF";
  const padH = options.paddingH ?? 6;
  const padV = options.paddingV ?? 4;
  const radius = options.radius ?? 1;

  const textObj = new FabricText(text, {
    fontSize,
    fontFamily,
    fontWeight: fw,
    fill: color,
    backgroundColor: "rgba(0,0,0,0)",
    textAlign: "center",
    originX: "center",
    originY: "center",
    objectCaching: false,
  });

  const textW = textObj.width ?? 0;
  const textH = textObj.height ?? 0;
  const bgW = textW + padH * 2;
  const bgH = textH + padV * 2;
  const r = Math.min(radius, bgH / 2);

  const bg = new Rect({
    originX: "center",
    originY: "center",
    width: bgW,
    height: bgH,
    fill: bgColor,
    rx: r,
    ry: r,
    stroke: options.stroke,
    strokeWidth: options.strokeWidth ?? 0,
    shadow: options.shadow ? new Shadow(options.shadow) : undefined,
    objectCaching: false,
  });

  return new Group([bg, textObj], {
    left: px,
    top: py,
    originX: (options.originX as any) ?? "center",
    originY: (options.originY as any) ?? "center",
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

/** Get the tip position of an arrow group (end of the triangle). */
function getArrowTipPoint(arrowGroup: Group, visualScale: number = 1): { x: number; y: number } {
  const angleDeg = arrowGroup.angle ?? 0;
  const rad = (angleDeg * Math.PI) / 180;
  const ax = arrowGroup.left ?? 0;
  const ay = arrowGroup.top ?? 0;
  const tipDist = scaledArrowHeight(visualScale) + clampScaled(ARROW_TRI_HEIGHT, visualScale, MIN_ARROW_TRI_H);
  return {
    x: ax - Math.sin(rad) * tipDist,
    y: ay + Math.cos(rad) * tipDist,
  };
}

/**
 * Compute the two perpendicular arrow angles (in fabric angle convention)
 * for a line defined by p0→p1.
 *
 * Fabric angle 0 = +Y (down), increases clockwise.
 * For a vector (vx, vy), fabric angle = atan2(-vx, vy).
 *
 * Perpendicular vectors of line p0→p1 (dx, dy):
 *   left  = (-dy, dx)   →  angle = atan2(dy, dx)
 *   right = (dy, -dx)   →  angle = atan2(-dy, -dx)
 */
function getPerpendicularArrowAngles(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
): [number, number] {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;

  const leftAngle = Math.atan2(dy, dx) * (180 / Math.PI);
  const rightAngle = Math.atan2(-dy, -dx) * (180 / Math.PI);
  return [leftAngle, rightAngle];
}

/**
 * Build all decoration objects for a line (arrows + labels) as an array.
 * All coordinates are RELATIVE to the group center (cx, cy).
 */
function buildLineDecorationObjects(
  rp0: { x: number; y: number },
  rp1: { x: number; y: number },
  eventType: CountingLineEventType,
  lineName: string,
  lineLabelDefs: LineLabelDefs,
  visualScale: number = 1,
): FabricObject[] {
  const [leftAngleDeg, rightAngleDeg] = getPerpendicularArrowAngles(rp0, rp1);

  const dx = rp1.x - rp0.x;
  const dy = rp1.y - rp0.y;
  const lineLen = Math.sqrt(dx * dx + dy * dy) || 1;
  const inset = Math.min(lineLen * 0.15, 20);
  const ux = dx / lineLen;
  const uy = dy / lineLen;
  const leftAngleRad = (leftAngleDeg * Math.PI) / 180;
  const rightAngleRad = (rightAngleDeg * Math.PI) / 180;
  const leftArrowPt = { x: rp0.x + ux * inset, y: rp0.y + uy * inset };
  const rightArrowPt = { x: rp1.x - ux * inset, y: rp1.y - uy * inset };

  const leftArrow = createArrowGroup(
    leftArrowPt.x, leftArrowPt.y,
    leftAngleDeg,
    LINE_STROKE,
    visualScale,
  );
  const rightArrow = createArrowGroup(
    rightArrowPt.x, rightArrowPt.y,
    rightAngleDeg,
    LINE_STROKE,
    visualScale,
  );

  // 방향 규약: coordinates[0]=입장(enter), coordinates[1]=퇴장(exit) — LTT/SSOT(flowmap) 및
  // 레거시와 동일. leftLabel 은 rp0(coord0) 쪽에 그려지므로, coord0 에 입장(base.right)이
  // 오도록 좌/우 라벨을 스왑한다. (vecX/vecY 정규화 불필요 — 방향은 순수 좌표 순서로 결정)
  const baseLabels = lineLabelDefs[eventType] ?? lineLabelDefs.default;
  const labelDefs = { left: baseLabels.right, right: baseLabels.left };

  const LABEL_GAP = Math.max(8, 20 * visualScale);
  const dirLabelStyle = scaleLabelStyle(LINE_DIR_LABEL_STYLE, visualScale);
  const nameLabelStyle = scaleLabelStyle(LINE_NAME_LABEL_STYLE, visualScale);
  const leftTip = getArrowTipPoint(leftArrow, visualScale);
  const leftLabel = createLabelGroup(labelDefs.left,
    leftTip.x - Math.sin(leftAngleRad) * LABEL_GAP,
    leftTip.y + Math.cos(leftAngleRad) * LABEL_GAP,
    { originX: "center", originY: "center", ...dirLabelStyle },
  );

  const rightTip = getArrowTipPoint(rightArrow, visualScale);
  const rightLabel = createLabelGroup(labelDefs.right,
    rightTip.x - Math.sin(rightAngleRad) * LABEL_GAP,
    rightTip.y + Math.cos(rightAngleRad) * LABEL_GAP,
    { originX: "center", originY: "center", ...dirLabelStyle },
  );

  const midX = (rp0.x + rp1.x) / 2;
  const midY = (rp0.y + rp1.y) / 2;
  const nameLabel = createLabelGroup(lineName, midX, midY, {
    originX: "center", originY: "center", ...nameLabelStyle,
  });

  return [leftArrow, rightArrow, leftLabel, rightLabel, nameLabel];
}

/**
 * PO QA 3차 y: 편집 모드에서도 방향 화살표/라벨이 보이도록.
 * line group 의 decorations 을 world-coord 로 만들어 canvas 에 직접 add 가능한 형태로 반환.
 * (buildLineDecorationObjects 는 group-relative 라 group 자식으로만 사용 가능)
 */
function buildWorldLineDecorationObjects(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  eventType: CountingLineEventType,
  lineName: string,
  lineLabelDefs: LineLabelDefs,
  visualScale: number = 1,
): FabricObject[] {
  const cx = (p0.x + p1.x) / 2;
  const cy = (p0.y + p1.y) / 2;
  const rp0 = { x: p0.x - cx, y: p0.y - cy };
  const rp1 = { x: p1.x - cx, y: p1.y - cy };
  const decorations = buildLineDecorationObjects(rp0, rp1, eventType, lineName, lineLabelDefs, visualScale);
  for (const obj of decorations) {
    obj.set({ left: (obj.left ?? 0) + cx, top: (obj.top ?? 0) + cy });
    obj.setCoords();
  }
  return decorations;
}

/**
 * Create a full line Group (polyline + decorations) as a single interactive object.
 * All child objects use coordinates relative to the group center.
 * The group is positioned at the centroid of the two endpoints.
 */
function createLineGroup(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  eventType: CountingLineEventType,
  lineName: string,
  lineLabelDefs: LineLabelDefs,
  visualScale: number = 1,
): Group {
  // Compute center — the group will be placed here
  const cx = (p0.x + p1.x) / 2;
  const cy = (p0.y + p1.y) / 2;

  // Convert to center-relative coordinates
  const rp0 = { x: p0.x - cx, y: p0.y - cy };
  const rp1 = { x: p1.x - cx, y: p1.y - cy };

  // Main line as a Polyline (not deprecated Line)
  const polyline = new Polyline([rp0, rp1], {
    stroke: LINE_STROKE,
    strokeWidth: scaledLineStroke(visualScale),
    fill: "transparent",
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  const decorations = buildLineDecorationObjects(rp0, rp1, eventType, lineName, lineLabelDefs, visualScale);

  // PO QA 3차 r: 라인 길이가 nameLabel 보다 짧으면 nameLabel 만 hide (도면 패턴 이식).
  // decorations 마지막 원소 = nameLabel (buildLineDecorationObjects 반환 순서)
  const dx = rp1.x - rp0.x;
  const dy = rp1.y - rp0.y;
  const lineLen = Math.sqrt(dx * dx + dy * dy);
  const nameLabel = decorations[decorations.length - 1] as Group | undefined;
  const showLabel = nameLabel ? lineLen > (nameLabel.width ?? 0) * 1.2 : true;
  const visibleDecorations = showLabel ? decorations : decorations.slice(0, decorations.length - 1);

  const group = new Group([polyline, ...visibleDecorations], {
    left: cx,
    top: cy,
    originX: "center",
    originY: "center",
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    borderColor: BORDER_COLOR,
    borderScaleFactor: 2,
    cornerColor: CORNER_COLOR,
    cornerStrokeColor: CORNER_STROKE_COLOR,
    cornerStyle: "rect",
    cornerSize: 8,
    transparentCorners: false,
    lockScalingFlip: true,
    objectCaching: false,
    subTargetCheck: false,
  });

  return group;
}

/**
 * Extract world-space endpoint coordinates from a line Group.
 * The polyline is the first child of the group.
 * Polyline points are in polyline-local space, so we need the full
 * polyline transform (which includes the group transform) to get world coords.
 */
function getLineGroupEndpoints(group: Group): { x: number; y: number }[] {
  const polyline = group.getObjects()[0] as Polyline;
  const pts = polyline.points!;
  // calcTransformMatrix on a group child includes the group's transform
  const plMatrix = polyline.calcTransformMatrix();
  return pts.map((pt) => {
    const world = util.transformPoint(new Point(pt.x, pt.y), plMatrix);
    return { x: world.x, y: world.y };
  });
}

// --- Component ---

export function SnapshotCanvas({
  coordinateEditingFinishRef,
  snapshotUrl,
  cameraId,
  countingLines,
  intervalZones,
  drawingRequest,
  drawingActiveCanvas = null,
  onDrawingClaim,
  drawingTarget,
  onDrawingTargetComplete,
  onDrawingTargetCancel,
  onDrawingCancel,
  selectedEventSource,
  onDrawingComplete,
  onLineCoordinatesChange,
  onZoneCoordinatesChange,
  onSelectionChange,
  onRequestDelete,
  onAddMemo,
  showLineZone,
  zoomButtonsBottomOffset = 12,
  memosOnSnapshot = [],
  memoPlacingMode = null,
  selectedMemoId = null,
  onMemoPlaced,
  onMemoMarkerClick,
  onMemoDragEnd,
  onDeleteMemoFromSnapshot,
  topPadding = 0,
  bottomPadding = 0,
  verticalAlign = "center",
}: SnapshotCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const { t } = useTranslation("shared");
  const { t: tDraft } = useTranslation("analysis-draft");
  const [, setZoom] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageNaturalSizeRef = useRef<ImageNaturalSize | null>(null);
  // PO QA 2차 o: topPadding/bottomPadding 을 ref 로 보관 (resize/load 핸들러에서 stale 없이 읽기 위함)
  const topPaddingRef = useRef(topPadding);
  topPaddingRef.current = topPadding;
  const bottomPaddingRef = useRef(bottomPadding);
  bottomPaddingRef.current = bottomPadding;
  const verticalAlignRef = useRef(verticalAlign);
  verticalAlignRef.current = verticalAlign;
  // QA-final: 사용자가 줌/이동/선택 등 조작을 시작했는지 여부.
  // 최초 로드 직후 오버레이 측정으로 padding 이 0→실제값으로 정착하는 동안에는 배경을 다시 fit 해야 하지만,
  // 사용자가 조작을 시작한 뒤(메모 오버레이·카메라 선택 등 padding 변경)에는 이미지 크기를 유지해야 함 → 이 플래그로 구분.
  const userInteractedRef = useRef(false);
  // bg refit 발생할 때마다 증가 — shape draw effect 의 deps 로 사용해 재계산 트리거
  const [bgFitVersion, setBgFitVersion] = useState(0);
  // B6: 마지막으로 fitBgToCanvas 를 실행한 시점의 verticalAlign.
  // topPadding/bottomPadding 변경(메모 오버레이 등)에선 리핏하지 않고,
  // verticalAlign 변경(viewMode 전환)에서만 리핏하기 위한 가드.
  const lastFitAlignRef = useRef(verticalAlign);
  // PO QA 3차 ab-4: 카메라 진입 시점의 bgScale 을 reference 로 잡아 모드 전환 시
  // 라벨/화살표/stroke 비례 축소 기준으로 사용. visualScale = currentBgScale / refBgScale.
  const refBgScaleRef = useRef<number | null>(null);

  const lineLabelDefs: LineLabelDefs = {
    default: {
      left: t("constants.countingLineEventType.default.left"),
      right: t("constants.countingLineEventType.default.right"),
    },
    visit: {
      left: t("constants.countingLineEventType.visit.left"),
      right: t("constants.countingLineEventType.visit.right"),
    },
    traffic: {
      left: t("constants.countingLineEventType.traffic.left"),
      right: t("constants.countingLineEventType.traffic.right"),
    },
  };

  const onDrawingCompleteRef = useRef(onDrawingComplete);
  onDrawingCompleteRef.current = onDrawingComplete;
  const onDrawingTargetCompleteRef = useRef(onDrawingTargetComplete);
  onDrawingTargetCompleteRef.current = onDrawingTargetComplete;
  const onDrawingCancelRef = useRef(onDrawingCancel);
  onDrawingCancelRef.current = onDrawingCancel;
  const onDrawingTargetCancelRef = useRef(onDrawingTargetCancel);
  onDrawingTargetCancelRef.current = onDrawingTargetCancel;
  const drawingTargetRef = useRef(drawingTarget);
  drawingTargetRef.current = drawingTarget;
  const drawingActiveCanvasRef = useRef(drawingActiveCanvas);
  drawingActiveCanvasRef.current = drawingActiveCanvas;
  const onDrawingClaimRef = useRef(onDrawingClaim);
  onDrawingClaimRef.current = onDrawingClaim;
  // 효과적 drawingRequest — drawingTarget 이 있으면 default 이벤트타입의 internal request 로 변환
  const effectiveDrawingRequest: DrawingRequest | null = drawingRequest
    ?? (drawingTarget ? { type: drawingTarget.type, eventType: "default" } : null);
  const onLineCoordinatesChangeRef = useRef(onLineCoordinatesChange);
  onLineCoordinatesChangeRef.current = onLineCoordinatesChange;
  const onZoneCoordinatesChangeRef = useRef(onZoneCoordinatesChange);
  onZoneCoordinatesChangeRef.current = onZoneCoordinatesChange;
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const onRequestDeleteRef = useRef(onRequestDelete);
  onRequestDeleteRef.current = onRequestDelete;
  const onMemoPlacedRef = useRef(onMemoPlaced);
  onMemoPlacedRef.current = onMemoPlaced;
  const onMemoMarkerClickRef = useRef(onMemoMarkerClick);
  onMemoMarkerClickRef.current = onMemoMarkerClick;
  const onMemoDragEndRef = useRef(onMemoDragEnd);
  onMemoDragEndRef.current = onMemoDragEnd;
  const onDeleteMemoFromSnapshotRef = useRef(onDeleteMemoFromSnapshot);
  onDeleteMemoFromSnapshotRef.current = onDeleteMemoFromSnapshot;
  const selectedEventSourceRef = useRef(selectedEventSource);
  selectedEventSourceRef.current = selectedEventSource;
  const memoPlacingModeRef = useRef(memoPlacingMode);
  memoPlacingModeRef.current = memoPlacingMode;

  const [markerToolbar, setMarkerToolbar] = useState<{
    type: "line" | "zone";
    id: number;
  } | null>(null);
  const markerToolbarRef = useRef<HTMLDivElement | null>(null);
  const onAddMemoRef = useRef(onAddMemo);
  onAddMemoRef.current = onAddMemo;

  // 메모 마커 선택 시 floating 삭제 툴바
  const [memoMarkerToolbar, setMemoMarkerToolbar] = useState<{ memoId: number } | null>(null);
  const memoMarkerToolbarRef = useRef<HTMLDivElement | null>(null);

  const drawingRequestRef = useRef<DrawingRequest | null>(effectiveDrawingRequest);
  drawingRequestRef.current = effectiveDrawingRequest;

  const drawingStateRef = useRef<{
    tempObjects: any[];
    points: { x: number; y: number }[];
    rectDragStart?: { x: number; y: number } | null; // F19
  } | null>(null);

  // F19: zone sub-mode (polygon vs rectangle) — ref for sync read in canvas event handlers
  // 기본값은 사각형 — 실무에서 사각형 구역이 훨씬 잦아, 다각형이 기본이면 매번 M 을 눌러야 했다.
  // 다각형이 필요하면 M 으로 전환하고, 전환한 모드는 이후 구역에도 유지된다.
  const zoneSubModeRef = useRef<"polygon" | "rectangle">("rectangle");
  const [zoneSubMode, setZoneSubMode] = useState<"polygon" | "rectangle">("rectangle");
  // F19: shift key tracking — ref for sync read in canvas event handlers
  const shiftHeldRef = useRef(false);

  // Track which line shapeGroup is currently in coordinate editing mode
  const activeCoordinateSgRef = useRef<any>(null);

  // Zone/line IDs to skip on next shapes rebuild (already updated in-place by finishEdit)
  const skipRebuildZoneIdsRef = useRef<Set<number>>(new Set());
  const skipRebuildLineIdsRef = useRef<Set<number>>(new Set());

  // Suppress selection:cleared events during programmatic canvas operations (e.g. canvas.clear())
  const suppressSelectionClearedRef = useRef(false);

  // --- Coordinate conversion ---
  const getBgImage = useCallback((): FabricImage | null => {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    return (
      (canvas
        .getObjects()
        .find((o) => (o as any).__isSnapshotBg === true) as FabricImage) ??
      null
    );
  }, []);

  function pixelToPercentStatic(p: { x: number; y: number }) {
    const ns = imageNaturalSizeRef.current;
    const bgObj = getBgImage();
    if (!ns || !bgObj) return { x: 0, y: 0 };
    const scale = bgObj.scaleX!;
    const scaledW = ns.width * scale;
    const scaledH = ns.height * scale;
    const tlX = bgObj.left! - scaledW / 2;
    const tlY = bgObj.top! - scaledH / 2;
    return {
      x: ((p.x - tlX) / (scale * ns.width)) * 100,
      y: ((p.y - tlY) / (scale * ns.height)) * 100,
    };
  }

  // PO QA 8차: 라벨/화살표 비례 축소용 visualScale.
  // 기존엔 기준(ref)을 "이미지 로드 시점의 bg scale"로 잡아, 둘다보기에서 로드되면 ref=작은값 →
  // visualScale≈1 → 라벨이 축소 안 돼 과대하게 보이고, 뷰 전환 시 ref 가 출렁여 깜빡였다.
  // → 기준을 **뷰-독립 결정적 값(가로 fit = canvasW / 이미지폭)** 으로 계산. 둘다보기에서도 안 흔들리고
  //   단독(가로 제약) 일 때 1, 둘다보기(세로 제약, 작은 이미지) 일 때 <1 로 비례 축소.
  function getVisualScale(): number {
    const bg = getBgImage();
    const cur = bg?.scaleX;
    const ns = imageNaturalSizeRef.current;
    const canvas = fabricRef.current;
    if (!cur || cur <= 0 || !ns || !canvas) return 1;
    const refScale = canvas.getWidth() / ns.width;
    if (refScale <= 0) return 1;
    return Math.min(1, cur / refScale);
  }

  function percentToPixelStatic(p: { x: number; y: number }) {
    const ns = imageNaturalSizeRef.current;
    const bgObj = getBgImage();
    if (!ns || !bgObj) return { x: 0, y: 0 };
    // PO QA 8차: 레거시 좌표 호환. 현재 포맷은 {x,y}(원본 이미지 기준 0~100 %).
    // 옛 데이터는 {_x,_y}(원본 이미지 픽셀값) 로 저장돼 있어 .x/.y 가 undefined → 안 그려짐.
    // → 이미지 natural size 로 %로 정규화해 동일하게 렌더 (원래 배치 그대로 보존).
    const anyP = p as { x?: number; y?: number; _x?: number; _y?: number };
    const xPct = typeof anyP.x === "number" ? anyP.x : typeof anyP._x === "number" ? (anyP._x / ns.width) * 100 : 0;
    const yPct = typeof anyP.y === "number" ? anyP.y : typeof anyP._y === "number" ? (anyP._y / ns.height) * 100 : 0;
    const scale = bgObj.scaleX!;
    const tlX = bgObj.left! - (ns.width * scale) / 2;
    const tlY = bgObj.top! - (ns.height * scale) / 2;
    return {
      x: (xPct / 100) * ns.width * scale + tlX,
      y: (yPct / 100) * ns.height * scale + tlY,
    };
  }

  // --- Canvas init ---
  useEffect(() => {
    const canvasEl = canvasRef.current;
    const container = containerRef.current;
    if (!canvasEl || !container) return;

    const { width, height } = container.getBoundingClientRect();
    const canvas = new Canvas(canvasEl, {
      width: width || 800,
      height: height || 600,
      selection: false,
      preserveObjectStacking: true,
      fireRightClick: true,
      stopContextMenu: true,
    });
    fabricRef.current = canvas;

    canvas.on("mouse:wheel", (opt) => {
      userInteractedRef.current = true;
      const delta = opt.e.deltaY;
      let newZoom = canvas.getZoom() * (delta > 0 ? 0.95 : 1.05);
      newZoom = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
      canvas.zoomToPoint(new Point(opt.e.offsetX, opt.e.offsetY), newZoom);
      setZoom(newZoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    let isPanning = false;
    let lastPos = { x: 0, y: 0 };

    canvas.on("mouse:down", (opt) => {
      userInteractedRef.current = true;
      const e = opt.e as MouseEvent;

      if (drawingRequestRef.current && e.button === 0) {
        // 4-3-2: 다른 캔버스(도면)가 이미 ownership 잡았으면 무시
        if (drawingActiveCanvasRef.current === "floorPlan") return;
        // 첫 클릭 시 ownership claim (drawingTarget 케이스는 새 라인 만드는 게 아니라서 claim 불필요)
        if (drawingActiveCanvasRef.current == null && !drawingTargetRef.current) {
          onDrawingClaimRef.current?.("snapshot");
        }
        // F19: zone + rectangle mode → rect drag
        const reqType = drawingRequestRef.current.type;
        if (reqType === "zone" && zoneSubModeRef.current === "rectangle") {
          handleRectDrawingMouseDown(canvas, opt.scenePoint);
          return;
        }
        handleDrawingClick(canvas, opt.scenePoint, shiftHeldRef.current);
        return;
      }

      // Memo placing mode: click anywhere to place memo (canvas objects 위 클릭도 허용)
      const currentMemoPlacing = memoPlacingModeRef.current;
      if (currentMemoPlacing && e.button === 0) {
        const percent = pixelToPercentStatic({ x: opt.scenePoint.x, y: opt.scenePoint.y });
        const clamped = getNormalizedPoint(percent);
        onMemoPlacedRef.current?.(currentMemoPlacing.memoId, clamped);
        return;
      }

      // 좌표 편집 모드에서 편집 중인 도형 외부를 클릭하면 즉시 편집 종료 (단일 클릭)
      if (e.button === 0 && activeCoordinateSgRef.current) {
        const activeSg = activeCoordinateSgRef.current;
        const clickedSg = opt.target ? (opt.target as any).__shapeGroup : null;
        const insideActive =
          clickedSg && clickedSg.type === activeSg.type && clickedSg.id === activeSg.id;
        if (!insideActive) {
          if (activeSg.type === "line") finishLineCoordinateEdit(activeSg);
          else if (activeSg.type === "zone") finishZoneCoordinateEdit(activeSg);
        }
      } else if (e.button === 0 && !activeCoordinateSgRef.current && coordinateEditingFinishRef.current) {
        // 6차 an: 자기 안 잡고 있지만 다른 캔버스(도면) 가 잡고 있으면 그쪽 종료
        coordinateEditingFinishRef.current();
      }

      if (e.button === 1 || (e.button === 0 && !opt.target && !activeCoordinateSgRef.current)) {
        isPanning = true;
        lastPos = { x: e.clientX, y: e.clientY };
        canvas.selection = false;
        // hoverCursor 까지 grabbing 으로 — 객체 위에서도 일관된 grabbing 유지 (fabric 은
        // mousemove 마다 target.hoverCursor || canvas.hoverCursor 로 재평가).
        // setCursor 즉시 호출 — 누른 직후 아직 안 움직였을 때도 grabbing 보이게.
        canvas.defaultCursor = "grabbing";
        canvas.hoverCursor = "grabbing";
        canvas.setCursor("grabbing");
        canvas.requestRenderAll();
      }
    });

    canvas.on("mouse:move", (opt) => {
      if (drawingRequestRef.current) {
        // F19: rect drag in progress
        if (drawingStateRef.current?.rectDragStart) {
          handleRectDrawingMouseMove(canvas, opt.scenePoint);
          return;
        }
        if (drawingStateRef.current) {
          handleDrawingMove(canvas, opt.scenePoint, shiftHeldRef.current);
        }
      }
      if (!isPanning) return;
      const e = opt.e as MouseEvent;
      const vpt = canvas.viewportTransform;
      vpt[4] += e.clientX - lastPos.x;
      vpt[5] += e.clientY - lastPos.y;
      lastPos = { x: e.clientX, y: e.clientY };
      canvas.requestRenderAll();
    });

    canvas.on("mouse:up", (opt) => {
      // F19: rect drag completion
      if (drawingStateRef.current?.rectDragStart) {
        handleRectDrawingMouseUp(canvas, opt.scenePoint);
      }
      if (isPanning) {
        isPanning = false;
        const req = drawingRequestRef.current;
        let cur: string | undefined;
        if (req) {
          if (req.type === "zone" && zoneSubModeRef.current === "rectangle") {
            cur = ZONE_RECT_DRAWING_CURSOR;
          } else {
            cur = req.type === "line" ? LINE_DRAWING_CURSOR : ZONE_DRAWING_CURSOR;
          }
        }
        // 평소 cursor 는 grab(pan 가능 신호) — 도면 캔버스와 통일 (PO QA 후속)
        canvas.defaultCursor = cur ?? "grab";
        canvas.hoverCursor = cur ?? "move";
        canvas.requestRenderAll();
      }
      // grabbing 으로 강제했던 커서 리셋 (mousedown 시 적용한 inline style 제거 → 객체별 hoverCursor 로 복귀)
      const upper = (canvas as any).upperCanvasEl as HTMLElement | undefined;
      if (upper && upper.style.cursor === "grabbing") {
        upper.style.cursor = "";
      }
    });

    canvas.on("mouse:dblclick", (opt) => {
      if (drawingRequestRef.current?.type === "zone") {
        handleDrawingDblClick(canvas);
        return;
      }

      // If something is currently in coordinate mode, exit it
      const activeSg = activeCoordinateSgRef.current;
      if (activeSg) {
        const clickedSg = opt.target ? (opt.target as any).__shapeGroup : null;
        if (!clickedSg || clickedSg === activeSg || clickedSg.id !== activeSg.id) {
          if (activeSg.type === "line") finishLineCoordinateEdit(activeSg);
          else if (activeSg.type === "zone") finishZoneCoordinateEdit(activeSg);
          return;
        }
      }

      // Enter coordinate mode on double-click
      if (opt.target) {
        const sg = (opt.target as any).__shapeGroup;
        if (sg?.type === "line" && sg.editingMode !== "coordinate") {
          enterLineCoordinateMode(sg, canvas);
        } else if (sg?.type === "zone" && sg.editingMode !== "coordinate") {
          enterZoneCoordinateMode(sg, canvas);
        }
      }
    });

    canvas.on("selection:created", (opt) => handleSelectionEvent(opt.selected));
    canvas.on("selection:updated", (opt) => handleSelectionEvent(opt.selected));
    canvas.on("selection:cleared", () => {
      if (suppressSelectionClearedRef.current) return;
      if (!activeCoordinateSgRef.current) {
        onSelectionChangeRef.current(null);
        setMarkerToolbar(null);
      }
    });
    // 클릭 도중 레이아웃 이동(뷰 자동 전환 등)으로 캔버스 요소가 움직이면 fabric 이 정지한
    // 포인터를 요소 이동량만큼의 drag 로 오인해 object:modified 를 발화한다 — 화면(client)
    // 기준 포인터 이동이 없으면 사용자 편집이 아니므로 좌표 반영을 차단 (FloorPlanViewer 와 동일 가드)
    let downClient: { x: number; y: number } | null = null;
    canvas.on("mouse:down", (opt) => {
      const e = opt.e as MouseEvent;
      downClient = typeof e.clientX === "number" ? { x: e.clientX, y: e.clientY } : null;
    });
    canvas.on("object:modified", (opt) => {
      const e = opt.e as MouseEvent | undefined;
      if (e && typeof e.clientX === "number" && downClient
        && Math.hypot(e.clientX - downClient.x, e.clientY - downClient.y) < 3) return;
      handleObjectModified(opt.target);
    });

    // Clamp shapes within image bounds during drag & sync zone labels
    canvas.on("object:moving", (opt) => {
      const target = opt.target;
      if (!target || !(target as any).__shapeGroup) return;
      const sg = (target as any).__shapeGroup;

      const bg = getBgImage();
      const ns = imageNaturalSizeRef.current;
      if (!bg || !ns) return;

      const scale = bg.scaleX!;
      const scaledW = ns.width * scale;
      const scaledH = ns.height * scale;
      const imgL = bg.left! - scaledW / 2;
      const imgT = bg.top! - scaledH / 2;
      const imgR = imgL + scaledW;
      const imgB = imgT + scaledH;

      target.setCoords();
      // 6차 ao: line 은 화살표 / zone 은 라벨까지 포함한 bbox 기준으로 clamp 하면 endpoint/vertex 가 가장자리에 못 닿음.
      // → size 모드일 때 line endpoint / zone vertex 만으로 clamp 계산. 좌표편집 모드 (handle drag) 는 기존 bbox 유지.
      let minX: number, maxX: number, minY: number, maxY: number;
      if (sg.type === "line" && sg.editingMode === "size") {
        const eps = getLineGroupEndpoints(target as Group);
        minX = Math.min(eps[0].x, eps[1].x);
        maxX = Math.max(eps[0].x, eps[1].x);
        minY = Math.min(eps[0].y, eps[1].y);
        maxY = Math.max(eps[0].y, eps[1].y);
      } else if (sg.type === "zone" && sg.editingMode === "size" && sg.polygon) {
        const poly = sg.polygon as Polygon;
        const matrix = poly.calcTransformMatrix();
        const poX = (poly as any).pathOffset?.x ?? 0;
        const poY = (poly as any).pathOffset?.y ?? 0;
        const xs: number[] = []; const ys: number[] = [];
        for (const pt of poly.points!) {
          const lx = pt.x - poX;
          const ly = pt.y - poY;
          xs.push(lx * matrix[0] + ly * matrix[2] + matrix[4]);
          ys.push(lx * matrix[1] + ly * matrix[3] + matrix[5]);
        }
        minX = Math.min(...xs); maxX = Math.max(...xs);
        minY = Math.min(...ys); maxY = Math.max(...ys);
      } else {
        const { tl, tr, bl, br } = target.aCoords!;
        minX = Math.min(tl.x, tr.x, bl.x, br.x);
        maxX = Math.max(tl.x, tr.x, bl.x, br.x);
        minY = Math.min(tl.y, tr.y, bl.y, br.y);
        maxY = Math.max(tl.y, tr.y, bl.y, br.y);
      }

      let dx = 0, dy = 0;
      if (minX < imgL) dx = imgL - minX;
      else if (maxX > imgR) dx = imgR - maxX;
      if (minY < imgT) dy = imgT - minY;
      else if (maxY > imgB) dy = imgB - maxY;

      if (dx !== 0 || dy !== 0) {
        target.set({ left: target.left! + dx, top: target.top! + dy });
        target.setCoords();
      }

      // Zone: move sibling objects (label) together with polygon
      // 좌표 편집 모드에서는 vertex handle 도 sg.__shapeGroup 으로 매칭돼서 sibling 으로 잡혀
      // 핸들 드래그 시 모든 객체가 같이 끌려가는 버그 → 편집 모드면 스킵
      if (
        sg.type === "zone"
        && (target as any).__prevLeft != null
        && activeCoordinateSgRef.current?.id !== sg.id
      ) {
        const moveDx = target.left! - (target as any).__prevLeft;
        const moveDy = target.top! - (target as any).__prevTop;
        const siblings = shapeObjectsRef.current.filter(
          (o) => (o as any).__shapeGroup === sg && o !== target,
        );
        for (const sib of siblings) {
          sib.set({ left: sib.left! + moveDx, top: sib.top! + moveDy });
          sib.setCoords();
        }
      }
      (target as any).__prevLeft = target.left!;
      (target as any).__prevTop = target.top!;
    });

    // ResizeObserver — 컨테이너 크기 변경 시 캔버스 dimension 갱신 + 배경 리핏.
    // TopOverlay 가 position:absolute 이므로 topPadding 변경은 컨테이너 크기에 영향 없음
    // → ResizeObserver 는 실제 레이아웃 변경(모드 전환, 윈도우 리사이즈, flex 정렬)에서만 발동.
    let wasHidden = false;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect;
        if (w === 0 || h === 0) { wasHidden = true; continue; }
        // 좌표 편집 중에 리핏이 끼어들면 아래 skipRebuild clear + refit 이
        // 편집 결과를 아직 커밋 안 된 옛 좌표로 되돌린다 (보기 모드 자동 전환 등).
        // → 리핏 전에 편집을 먼저 커밋한다.
        finishActiveCoordinateEdit();
        canvas.setDimensions({ width: w, height: h });
        const bgImg = canvas
          .getObjects()
          .find((o) => (o as any).__isSnapshotBg === true) as FabricImage | undefined;
        const ns = imageNaturalSizeRef.current;
        if (bgImg && ns) {
          // 모드 전환(verticalAlign 변경 또는 hidden→visible): viewport 줌 리셋
          const modeSwitch = wasHidden || verticalAlignRef.current !== lastFitAlignRef.current;
          if (modeSwitch) {
            canvas.setViewportTransform([1, 0, 0, 1, 0, 0] as TMat2D);
            setZoom(1);
            lastFitAlignRef.current = verticalAlignRef.current;
          }
          const oldLeft = bgImg.left!;
          const oldTop = bgImg.top!;
          const oldScale = bgImg.scaleX!;
          fitBgToCanvas(canvas, bgImg, ns, topPaddingRef.current, bottomPaddingRef.current, verticalAlignRef.current);
          skipRebuildZoneIdsRef.current.clear();
          skipRebuildLineIdsRef.current.clear();
          repositionObjectsAfterBgRefit(canvas, ns, oldLeft, oldTop, oldScale, "__isSnapshotBg");
          setBgFitVersion((v) => v + 1);
        }
        wasHidden = false;
        canvas.requestRenderAll();
      }
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      // 편집 중 언마운트되면 finish 가 안 불려 (a) 편집 결과가 날아가고
      // (b) coordinateEditingFinishRef 가 non-null 로 남아 저장 버튼이 계속 잠긴다
      finishActiveCoordinateEdit();
      canvas.dispose();
      fabricRef.current = null;
    };
  }, []);

  // --- Selection handler ---
  function handleSelectionEvent(selected: any[] | undefined) {
    // Don't report selection changes during coordinate editing mode
    if (activeCoordinateSgRef.current) return;

    if (!selected || selected.length === 0) {
      onSelectionChangeRef.current(null);
      onMemoMarkerClickRef.current?.(null);
      setMarkerToolbar(null);
      return;
    }
    const target = selected[0];
    if (target instanceof MemoMarker) {
      onMemoMarkerClickRef.current?.(target.memoId);
      setMarkerToolbar(null);
      return;
    }
    const group = (target as any).__shapeGroup;
    if (group?.type === "line") {
      onSelectionChangeRef.current({ type: "line", id: group.id });
      setMarkerToolbar({ type: "line", id: group.id });
    } else if (group?.type === "zone") {
      onSelectionChangeRef.current({ type: "zone", id: group.id });
      setMarkerToolbar({ type: "zone", id: group.id });
    } else {
      onSelectionChangeRef.current(null);
      setMarkerToolbar(null);
    }
  }

  // --- Object modified handler ---
  function handleObjectModified(target: any) {
    if (!target) return;

    // MemoMarker drag end
    if (target instanceof MemoMarker) {
      const percent = pixelToPercentStatic({ x: target.left!, y: target.top! });
      const clamped = getNormalizedPoint(percent);
      onMemoDragEndRef.current?.(target.memoId, clamped);
      return;
    }

    const sg = (target as any).__shapeGroup;
    if (!sg) return;

    const bgObj = getBgImage();
    const ns = imageNaturalSizeRef.current;

    if (sg.type === "line") {
      if (sg.editingMode === "coordinate") {
        // In coordinate mode, endpoints are being dragged.
        // Don't finish — just let the user keep editing.
        // Coordinates will be synced when exiting coordinate mode via double-click.
      } else {
        // Size mode — extract world endpoints from the group transform
        const lineGroup = sg.fabricGroup as Group;
        let worldPts = getLineGroupEndpoints(lineGroup);

        // Clamp endpoints that went outside the image
        if (bgObj && ns) {
          const clamped = worldPts.map((p) => clampToImageBounds(p, bgObj, ns));
          const needsRebuild = worldPts.some(
            (p, i) => p.x !== clamped[i].x || p.y !== clamped[i].y,
          );
          if (needsRebuild) {
            worldPts = clamped;
            // Remove old group and rebuild at clamped positions
            const canvas = fabricRef.current!;
            canvas.remove(lineGroup);
            const idx = shapeObjectsRef.current.indexOf(lineGroup);
            if (idx !== -1) shapeObjectsRef.current.splice(idx, 1);

            const newGroup = createLineGroup(
              worldPts[0], worldPts[1],
              sg.eventType, sg.lineName, lineLabelDefs,
              getVisualScale(),
            );
            (newGroup as any).__shapeGroup = sg;
            sg.fabricGroup = newGroup;
            canvas.add(newGroup);
            shapeObjectsRef.current.push(newGroup);
            canvas.setActiveObject(newGroup);
            canvas.requestRenderAll();
          }
        }

        const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p)));
        onLineCoordinatesChangeRef.current(sg.id, coords);
      }
    } else if (sg.type === "zone") {
      if (sg.editingMode === "coordinate") {
        // Vertex handles being dragged — don't finish yet
      } else if (sg.polygon) {
        const poly = sg.polygon as Polygon;
        const matrix = poly.calcTransformMatrix();
        const poX = (poly as any).pathOffset?.x ?? 0;
        const poY = (poly as any).pathOffset?.y ?? 0;
        let worldPts = poly.points!.map((pt) => {
          const lx = pt.x - poX;
          const ly = pt.y - poY;
          return {
            x: lx * matrix[0] + ly * matrix[2] + matrix[4],
            y: lx * matrix[1] + ly * matrix[3] + matrix[5],
          };
        });

        // Clamp vertices that went outside the image
        if (bgObj && ns) {
          const clamped = worldPts.map((p) => clampToImageBounds(p, bgObj, ns));
          const needsRebuild = worldPts.some(
            (p, i) => p.x !== clamped[i].x || p.y !== clamped[i].y,
          );
          if (needsRebuild) {
            worldPts = clamped;
            const canvas = fabricRef.current!;

            // Remove old polygon + label
            const oldObjs = shapeObjectsRef.current.filter(
              (o) => (o as any).__shapeGroup === sg,
            );
            for (const obj of oldObjs) {
              canvas.remove(obj);
              const idx = shapeObjectsRef.current.indexOf(obj);
              if (idx !== -1) shapeObjectsRef.current.splice(idx, 1);
            }

            // Rebuild polygon at clamped positions
            const cx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
            const cy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
            const relPts = worldPts.map((p) => ({ x: p.x - cx, y: p.y - cy }));
            const vsForZone = getVisualScale();
            const newPoly = new Polygon(relPts, {
              originX: "center", originY: "center",
              fill: ZONE_FILL, stroke: ZONE_STROKE,
              strokeWidth: clampScaled(2, vsForZone, MIN_STROKE_WIDTH),
              strokeUniform: true,
              selectable: true, evented: true, hasControls: true, hasBorders: true,
              borderColor: BORDER_COLOR, borderScaleFactor: 2,
              cornerColor: CORNER_COLOR, cornerStrokeColor: CORNER_STROKE_COLOR,
              cornerStyle: "rect", cornerSize: 8, transparentCorners: false,
              lockScalingFlip: true,
            });
            const polyPo = (newPoly as any).pathOffset as { x: number; y: number };
            newPoly.set({ left: cx + polyPo.x, top: cy + polyPo.y });
            newPoly.setCoords();
            (newPoly as any).__shapeGroup = sg;
            sg.polygon = newPoly;

            const nameLabel = createLabelGroup(
              sg.zoneName ?? "", cx, cy,
              { originX: "center", originY: "center", ...scaleLabelStyle(ZONE_LABEL_STYLE, vsForZone) },
            );
            (nameLabel as any).__shapeGroup = sg;
            // PO QA 3차 r: 구역 bbox 가 라벨보다 작으면 라벨 hide
            const lpw = newPoly.width ?? 0;
            const lph = newPoly.height ?? 0;
            const llw = nameLabel.width ?? 0;
            const llh = nameLabel.height ?? 0;
            nameLabel.set({ visible: lpw > llw * 1.2 && lph > llh * 1.2 });

            canvas.add(newPoly);
            canvas.add(nameLabel);
            shapeObjectsRef.current.push(newPoly, nameLabel);
            canvas.setActiveObject(newPoly);
            canvas.requestRenderAll();
          }
        }

        // PO QA 3차 r: 사이즈 조정(needsRebuild=false 포함) 시 라벨 visibility / 위치 재평가 (도면 패턴 이식)
        const zoneLabel = shapeObjectsRef.current.find(
          (o) => (o as any).__shapeGroup === sg && o !== sg.polygon,
        );
        if (zoneLabel && sg.polygon) {
          const effW = (sg.polygon.width ?? 0) * Math.abs(sg.polygon.scaleX ?? 1);
          const effH = (sg.polygon.height ?? 0) * Math.abs(sg.polygon.scaleY ?? 1);
          const lw = (zoneLabel as any).width ?? 0;
          const lh = (zoneLabel as any).height ?? 0;
          (zoneLabel as any).set({ visible: effW > lw * 1.2 && effH > lh * 1.2 });
          const ncx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
          const ncy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
          (zoneLabel as any).set({ left: ncx, top: ncy });
          (zoneLabel as any).setCoords();
        }

        const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p)));
        onZoneCoordinatesChangeRef.current(sg.id, coords);
      }
    }
  }

  // --- Escape key handler: cancel drawing mode + M key (F19 zone sub-mode toggle) ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        if (target?.isContentEditable) return;

        if (drawingRequestRef.current) {
          const canvas = fabricRef.current;
          if (canvas) {
            clearDrawingTemp(canvas);
          }
          onDrawingCancelRef.current?.();
          return;
        }
        if (drawingTargetRef.current) {
          const canvas = fabricRef.current;
          if (canvas) {
            clearDrawingTemp(canvas);
          }
          onDrawingTargetCancelRef.current?.();
          return;
        }
        return;
      }
      // F19: M key — toggle zone sub-mode (polygon ↔ rectangle). Use e.code for IME independence (한글 모드에서도 동작)
      if (e.code === "KeyM" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // 텍스트 입력 중이면 단축키 비활성
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return;
        const activeType = drawingRequestRef.current?.type ?? drawingTargetRef.current?.type;
        if (activeType === "zone") {
          const canvas = fabricRef.current;
          if (canvas) {
            // Clear any in-progress drawing on mode switch
            if (drawingStateRef.current) {
              clearDrawingTemp(canvas);
            }
            // Toggle sub-mode
            const next = zoneSubModeRef.current === "polygon" ? "rectangle" : "polygon";
            zoneSubModeRef.current = next;
            setZoneSubMode(next);
            // Update cursor immediately (setCursor forces instant visual update)
            const cur = next === "rectangle" ? ZONE_RECT_DRAWING_CURSOR : ZONE_DRAWING_CURSOR;
            canvas.defaultCursor = cur;
            canvas.hoverCursor = cur;
            canvas.setCursor(cur);
            canvas.requestRenderAll();
          }
          e.preventDefault();
          return;
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // F19: Shift key tracking for line snap
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { shiftHeldRef.current = e.shiftKey; };
    const onKeyUp = (e: KeyboardEvent) => { shiftHeldRef.current = e.shiftKey; };
    const onBlur = () => { shiftHeldRef.current = false; };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // --- Backspace / Delete key handler ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Backspace" && e.key !== "Delete") return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (target?.isContentEditable) return;
      if (activeCoordinateSgRef.current) return;

      const canvas = fabricRef.current;
      if (!canvas) return;
      const active = canvas.getActiveObject();
      if (!active) return;
      const sg = (active as any).__shapeGroup;
      if (!sg) return;
      if (sg.type === "line" || sg.type === "zone") {
        onRequestDeleteRef.current?.(sg.type, sg.id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleToolbarDelete = useCallback(() => {
    if (!markerToolbar) return;
    onRequestDeleteRef.current?.(markerToolbar.type, markerToolbar.id);
  }, [markerToolbar]);

  const handleToolbarSwap = useCallback(() => {
    if (!markerToolbar || markerToolbar.type !== "line") return;
    const canvas = fabricRef.current;
    if (!canvas) return;
    const obj = canvas
      .getObjects()
      .find((o) => (o as any).__shapeGroup?.type === "line" && (o as any).__shapeGroup?.id === markerToolbar.id);
    if (obj) flipLineDirection(obj as Group);
  }, [markerToolbar]);

  // --- Marker toolbar 위치 동기화 (선택된 라인/존 의 bbox 위 floating) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const syncPosition = () => {
      const tb = markerToolbar;
      const el = markerToolbarRef.current;
      if (!el || !tb) return;
      const obj = canvas
        .getObjects()
        .find((o) => {
          const sg = (o as any).__shapeGroup;
          return sg?.type === tb.type && sg?.id === tb.id;
        });
      if (!obj) return;
      // viewport transform 직접 적용 — pan/zoom 즉시 추종
      const vpt = canvas.viewportTransform;
      const bb = (obj as any).getBoundingRect?.();
      if (!bb) return;
      // bb 는 캔버스 로컬 좌표 (이미 vpt 반영됨)
      el.style.left = `${bb.left}px`;
      el.style.top = `${bb.top}px`;
      void vpt;
    };

    syncPosition();
    canvas.on("after:render", syncPosition);
    canvas.on("object:moving", syncPosition);
    return () => {
      canvas.off("after:render", syncPosition);
      canvas.off("object:moving", syncPosition);
    };
  }, [markerToolbar]);

  // --- Line coordinate editing mode ---

  function enterLineCoordinateMode(sg: any, canvas: Canvas) {
    const oldGroup = sg.fabricGroup as Group;
    canvas.discardActiveObject();

    // Get current world-space endpoints
    const worldPts = getLineGroupEndpoints(oldGroup);

    // Remove old group
    canvas.remove(oldGroup);
    const idx = shapeObjectsRef.current.indexOf(oldGroup);
    if (idx !== -1) shapeObjectsRef.current.splice(idx, 1);

    // Create a visible line (non-interactive, just for display)
    const displayLine = new Line(
      [worldPts[0].x, worldPts[0].y, worldPts[1].x, worldPts[1].y],
      {
        stroke: LINE_STROKE,
        strokeWidth: scaledLineStroke(getVisualScale()),
        selectable: false,
        evented: false,
        objectCaching: false,
      },
    );

    // PO QA 3차: 원 핸들도 배경 scale 비례 축소 (단독 모드는 그대로, 둘 다 모드만 작게)
    const handleVS = getVisualScale();
    // Create two draggable endpoint circles
    const makeEndpoint = (pt: { x: number; y: number }) =>
      new Circle({
        left: pt.x,
        top: pt.y,
        radius: clampScaled(6, handleVS, 3),
        fill: CORNER_COLOR,
        stroke: CORNER_STROKE_COLOR,
        strokeWidth: clampScaled(2, handleVS, 1),
        originX: "center",
        originY: "center",
        selectable: true,
        evented: true,
        hasControls: false,
        hasBorders: false,
        hoverCursor: "crosshair",
        moveCursor: "crosshair",
        objectCaching: false,
      });

    const ep0 = makeEndpoint(worldPts[0]);
    const ep1 = makeEndpoint(worldPts[1]);

    // Tag them so we know they belong to this line's coordinate mode
    (displayLine as any).__shapeGroup = sg;
    (ep0 as any).__shapeGroup = sg;
    (ep1 as any).__shapeGroup = sg;
    (ep0 as any).__endpointIndex = 0;
    (ep1 as any).__endpointIndex = 1;

    // PO QA 3차 y: 편집 모드에서 방향 화살표 + 입/퇴장 라벨 유지 (nameLabel 만 hide).
    // 결과 0-3 = leftArrow, rightArrow, leftLabel, rightLabel / 4 = nameLabel
    let arrowObjs: FabricObject[] = buildWorldLineDecorationObjects(
      worldPts[0], worldPts[1],
      sg.eventType, sg.lineName, lineLabelDefs,
      getVisualScale(),
    ).slice(0, 4);
    for (const obj of arrowObjs) (obj as any).__shapeGroup = sg;

    // Clamp endpoint to image bounds and update display line in real-time
    const clampEndpoint = (ep: Circle) => {
      const bgObj = getBgImage();
      const ns = imageNaturalSizeRef.current;
      if (bgObj && ns) {
        const clamped = clampToImageBounds({ x: ep.left!, y: ep.top! }, bgObj, ns);
        ep.set({ left: clamped.x, top: clamped.y });
      }
    };
    const onEndpointMove = (ep: Circle) => {
      clampEndpoint(ep);
      displayLine.set({
        x1: ep0.left!,
        y1: ep0.top!,
        x2: ep1.left!,
        y2: ep1.top!,
      });
      for (const obj of arrowObjs) {
        canvas.remove(obj);
        const di = shapeObjectsRef.current.indexOf(obj);
        if (di !== -1) shapeObjectsRef.current.splice(di, 1);
      }
      arrowObjs = buildWorldLineDecorationObjects(
        { x: ep0.left!, y: ep0.top! },
        { x: ep1.left!, y: ep1.top! },
        sg.eventType, sg.lineName, lineLabelDefs,
        getVisualScale(),
      ).slice(0, 4);
      for (const obj of arrowObjs) {
        (obj as any).__shapeGroup = sg;
        canvas.add(obj);
        shapeObjectsRef.current.push(obj);
      }
      sg.coordinateParts.arrowObjs = arrowObjs;
      canvas.requestRenderAll();
    };
    // Cursor 위치로 endpoint 중심을 강제. grab offset 으로 endpoint 가 이미지 경계까지 못 가던 문제 해결 (PO QA 3차 ab-1)
    const movingWithCursor = (ep: Circle) => (opt: any) => {
      const ptr = canvas.getScenePoint(opt.e);
      const bgObj = getBgImage();
      const ns = imageNaturalSizeRef.current;
      if (bgObj && ns) {
        const clamped = clampToImageBounds(ptr, bgObj, ns);
        ep.set({ left: clamped.x, top: clamped.y });
      }
      onEndpointMove(ep);
    };
    ep0.on("moving", movingWithCursor(ep0));
    ep1.on("moving", movingWithCursor(ep1));
    const setGrabbing = () => {
      const upper = (canvas as any).upperCanvasEl as HTMLElement | undefined;
      if (upper) upper.style.cursor = "crosshair";
    };
    ep0.on("mousedown", setGrabbing);
    ep1.on("mousedown", setGrabbing);

    sg.editingMode = "coordinate";
    sg.coordinateParts = { displayLine, ep0, ep1, arrowObjs };
    activeCoordinateSgRef.current = sg;
    // 6차 an: cross-canvas 자동 종료용 — 도면에서 클릭 시 호출되어 자기 finishEdit
    coordinateEditingFinishRef.current = () => finishLineCoordinateEdit(sg);

    const objs = [displayLine, ep0, ep1, ...arrowObjs];
    for (const obj of objs) {
      canvas.add(obj);
      shapeObjectsRef.current.push(obj);
    }

    canvas.setActiveObject(ep0);
    canvas.requestRenderAll();
  }

  function finishLineCoordinateEdit(sg: any) {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const parts = sg.coordinateParts;
    if (!parts) return;

    const { displayLine, ep0, ep1, arrowObjs } = parts as {
      displayLine: Line;
      ep0: Circle;
      ep1: Circle;
      arrowObjs?: FabricObject[];
    };

    // Extract world-space endpoints from circle positions (pixel coords)
    const worldPts = [
      { x: ep0.left!, y: ep0.top! },
      { x: ep1.left!, y: ep1.top! },
    ];

    // Remove coordinate mode objects
    canvas.discardActiveObject();
    for (const obj of [displayLine, ep0, ep1, ...(arrowObjs ?? [])]) {
      canvas.remove(obj);
      const i = shapeObjectsRef.current.indexOf(obj);
      if (i !== -1) shapeObjectsRef.current.splice(i, 1);
    }

    // Rebuild line group at exact pixel positions (no percent round-trip)
    const newGroup = createLineGroup(
      worldPts[0], worldPts[1],
      sg.eventType,
      sg.lineName,
      lineLabelDefs,
      getVisualScale(),
    );
    (newGroup as any).__shapeGroup = sg;
    sg.fabricGroup = newGroup;

    canvas.add(newGroup);
    shapeObjectsRef.current.push(newGroup);

    // Mark this line to skip on next shapes rebuild
    skipRebuildLineIdsRef.current.add(sg.id);

    const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p)));

    sg.editingMode = "size";
    sg.coordinateParts = null;
    activeCoordinateSgRef.current = null;
    coordinateEditingFinishRef.current = null;

    onLineCoordinatesChangeRef.current(sg.id, coords);
  }

  // --- Zone coordinate editing mode ---

  function enterZoneCoordinateMode(sg: any, canvas: Canvas) {
    const poly = sg.polygon as Polygon;
    canvas.discardActiveObject();

    // Extract world-space vertices.
    // Must subtract pathOffset before transforming — Fabric uses pathOffset
    // internally for bbox-center positioning, and we need to undo it to get
    // the correct world coordinates.
    const matrix = poly.calcTransformMatrix();
    const poX = (poly as any).pathOffset?.x ?? 0;
    const poY = (poly as any).pathOffset?.y ?? 0;
    const worldPts = poly.points!.map((pt) => {
      const w = util.transformPoint(new Point(pt.x - poX, pt.y - poY), matrix);
      return { x: w.x, y: w.y };
    });

    // Hide original polygon + label
    poly.set({ visible: false, selectable: false, evented: false });
    const nameLabel = shapeObjectsRef.current.find(
      (o) => (o as any).__shapeGroup === sg && o !== poly,
    );
    if (nameLabel) (nameLabel as any).set({ visible: false });

    // Create fill display using Polyline with absolute coords
    // (Polyline auto-positions correctly with left=pathOffset.x, top=pathOffset.y)
    const displayFill = new Polyline(
      worldPts.map((p) => ({ x: p.x, y: p.y })),
      {
        fill: ZONE_FILL,
        stroke: "transparent",
        selectable: false,
        evented: false,
        objectCaching: false,
      },
    );
    (displayFill as any).__shapeGroup = sg;

    // Create name label at centroid
    const labelCx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
    const labelCy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
    const vsForZoneEdit = getVisualScale();
    const displayLabel = createLabelGroup(
      sg.zoneName ?? "",
      labelCx, labelCy,
      { originX: "center", originY: "center", ...scaleLabelStyle(ZONE_LABEL_STYLE, vsForZoneEdit) },
    );
    (displayLabel as any).__shapeGroup = sg;

    // Create edge lines
    const edgeLines: Line[] = [];
    for (let i = 0; i < worldPts.length; i++) {
      const a = worldPts[i];
      const b = worldPts[(i + 1) % worldPts.length];
      const edge = new Line([a.x, a.y, b.x, b.y], {
        stroke: ZONE_STROKE,
        strokeWidth: clampScaled(2, vsForZoneEdit, MIN_STROKE_WIDTH),
        selectable: false,
        evented: false,
        objectCaching: false,
      });
      (edge as any).__shapeGroup = sg;
      edgeLines.push(edge);
    }

    // Create draggable vertex handles
    const zoneVS = getVisualScale();
    const vertexHandles: Circle[] = worldPts.map((pt) =>
      new Circle({
        left: pt.x,
        top: pt.y,
        radius: clampScaled(6, zoneVS, 3),
        fill: CORNER_COLOR,
        stroke: CORNER_STROKE_COLOR,
        strokeWidth: clampScaled(2, zoneVS, 1),
        originX: "center",
        originY: "center",
        selectable: true,
        evented: true,
        hasControls: false,
        hasBorders: false,
        hoverCursor: "crosshair",
        moveCursor: "crosshair",
        objectCaching: false,
      }),
    );
    for (const vh of vertexHandles) {
      (vh as any).__shapeGroup = sg;
    }

    // Clamp vertex handles to image bounds and update display objects
    const onVertexMove = (movedHandle: Circle) => {
      const bgObj = getBgImage();
      const ns = imageNaturalSizeRef.current;
      if (bgObj && ns) {
        const clamped = clampToImageBounds({ x: movedHandle.left!, y: movedHandle.top! }, bgObj, ns);
        movedHandle.set({ left: clamped.x, top: clamped.y });
      }
      const pts = vertexHandles.map((vh) => ({ x: vh.left!, y: vh.top! }));
      // Update edge lines
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        edgeLines[i].set({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }
      // Update fill polyline
      displayFill.points = pts.map((p) => ({ x: p.x, y: p.y }));
      (displayFill as any).initialized = false;
      displayFill.setDimensions();
      displayFill.set({ left: (displayFill as any).pathOffset.x, top: (displayFill as any).pathOffset.y });
      displayFill.setCoords();
      // Update label position
      const ncx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const ncy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      displayLabel.set({ left: ncx, top: ncy });
      displayLabel.setCoords();
      canvas.requestRenderAll();
    };
    for (const vh of vertexHandles) {
      vh.on("moving", (opt: any) => {
        // 사용자의 cursor 위치를 직접 추적해 handle 중심이 이미지 경계까지 도달하게 함.
        // fabric 기본 drag 는 grab offset(원 가장자리 잡은 경우) 만큼 빼서 handle.left=cursor-offset 으로 계산하므로
        // cursor 가 이미지 경계에 닿아도 handle 중심은 (imageEdge - radius) 에서 멈춤 → 외곽선이 가장자리까지 못 도달.
        // (PO QA 3차 ab-1)
        const ptr = canvas.getScenePoint(opt.e);
        const bgObj = getBgImage();
        const ns = imageNaturalSizeRef.current;
        if (bgObj && ns) {
          const clamped = clampToImageBounds(ptr, bgObj, ns);
          vh.set({ left: clamped.x, top: clamped.y });
        }
        onVertexMove(vh);
      });
      vh.on("mousedown", () => {
        // 클릭 즉시 crosshair 커서 유지 (fabric moveCursor 는 실제 이동이 일어나야 적용됨)
        const upper = (canvas as any).upperCanvasEl as HTMLElement | undefined;
        if (upper) upper.style.cursor = "crosshair";
      });
    }
    sg.editingMode = "coordinate";
    sg.coordinateParts = { displayFill, displayLabel, edgeLines, vertexHandles };
    activeCoordinateSgRef.current = sg;
    // 6차 an: cross-canvas 자동 종료용
    coordinateEditingFinishRef.current = () => finishZoneCoordinateEdit(sg);

    const allObjs = [displayFill, ...edgeLines, ...vertexHandles];
    for (const obj of allObjs) {
      canvas.add(obj);
      shapeObjectsRef.current.push(obj);
    }

    // setActiveObject(vh[0]) 호출 시 fabric 이 polygon (이미 active 였던) + vh[0] 을 묶어
    // ActiveSelection 만들어 group drag 발동하는 케이스 발견 (모든 vertex+polygon 같은 delta 로 이동)
    // → 사용자가 명시적으로 vertex 클릭하면 fabric 이 자동으로 single-active 만들도록 함
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }

  /**
   * 이 캔버스가 좌표 편집 중이면 커밋하고 편집 상태를 정리한다.
   * 편집 중이 아니면 아무것도 하지 않는다 (호출 측에서 조건 없이 부를 수 있게).
   */
  function finishActiveCoordinateEdit() {
    const activeSg = activeCoordinateSgRef.current;
    if (!activeSg) return;
    if (activeSg.type === "line") finishLineCoordinateEdit(activeSg);
    else if (activeSg.type === "zone") finishZoneCoordinateEdit(activeSg);
  }

  function finishZoneCoordinateEdit(sg: any) {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const parts = sg.coordinateParts;
    if (!parts) return;

    const { displayFill, displayLabel, edgeLines, vertexHandles } = parts as {
      displayFill: Polyline;
      displayLabel: Group;
      edgeLines: Line[];
      vertexHandles: Circle[];
    };

    // Extract world-space vertices from handle positions
    const worldPts = vertexHandles.map((vh) => ({
      x: vh.left!,
      y: vh.top!,
    }));

    // Remove all coordinate mode objects
    canvas.discardActiveObject();
    for (const obj of [displayFill, displayLabel, ...edgeLines, ...vertexHandles]) {
      canvas.remove(obj);
      const i = shapeObjectsRef.current.indexOf(obj);
      if (i !== -1) shapeObjectsRef.current.splice(i, 1);
    }

    // Update original polygon points in-place using centroid-relative coords
    // + bbox-center positioning (same pattern as initial creation)
    const poly = sg.polygon as Polygon;
    if (poly) {
      const cx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
      const cy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
      poly.points = worldPts.map((p) => ({ x: p.x - cx, y: p.y - cy }));
      poly.set({
        originX: "center", originY: "center",
        scaleX: 1, scaleY: 1, angle: 0, skewX: 0, skewY: 0,
        visible: true, stroke: ZONE_STROKE,
        selectable: true, evented: true,
        hasControls: true, hasBorders: true,
      });
      (poly as any).initialized = false;
      poly.setDimensions();
      // Position at centroid + pathOffset (bbox center) for exact visual placement
      const rpo = (poly as any).pathOffset as { x: number; y: number };
      poly.set({ left: cx + rpo.x, top: cy + rpo.y });
      poly.setCoords();
    }

    // Restore label at new centroid
    const nameLabel = shapeObjectsRef.current.find(
      (o) => (o as any).__shapeGroup === sg && o !== poly,
    );
    if (nameLabel) {
      const cx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
      const cy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
      // PO QA 3차 r: 새 bbox 가 라벨보다 작으면 라벨 hide
      const effW = (poly?.width ?? 0) * Math.abs(poly?.scaleX ?? 1);
      const effH = (poly?.height ?? 0) * Math.abs(poly?.scaleY ?? 1);
      const lw = (nameLabel as any).width ?? 0;
      const lh = (nameLabel as any).height ?? 0;
      (nameLabel as any).set({
        left: cx,
        top: cy,
        visible: effW > lw * 1.2 && effH > lh * 1.2,
      });
      (nameLabel as any).setCoords();
    }

    // 편집한 구역은 rebuild 로 다시 그리게 둔다 (skip 하지 않는다).
    // 위의 in-place 갱신은 폴리곤의 points 만 새 좌표로 바꿀 뿐 외곽선이 옛 bounding box 를
    // 그대로 유지해서, 사각형을 삼각형으로 고쳐도 사각형 테두리가 남았다. skip 까지 걸려 있어
    // 그 잘못된 그림이 다음 rebuild 전까지 계속 보였고, 사용자에겐 "수정이 원래대로 돌아온다"로 보였다.
    // 좌표 자체는 아래 onZoneCoordinatesChange 로 정확히 반영되므로, 그리기는 정상 경로에 맡긴다.

    // Update coordinates — triggers React re-render → 이 구역이 새 좌표로 다시 그려진다
    const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p)));

    sg.editingMode = "size";
    sg.coordinateParts = null;
    activeCoordinateSgRef.current = null;
    coordinateEditingFinishRef.current = null;

    onZoneCoordinatesChangeRef.current(sg.id, coords);
  }

  // --- Drawing ---

  function handleDrawingClick(canvas: Canvas, worldPt: Point, shiftHeld?: boolean) {
    const req = drawingRequestRef.current;
    if (!req) return;

    // Clamp click point to image bounds
    const bgObj = getBgImage();
    const ns = imageNaturalSizeRef.current;
    if (bgObj && ns) {
      const clamped = clampToImageBounds({ x: worldPt.x, y: worldPt.y }, bgObj, ns);
      worldPt = new Point(clamped.x, clamped.y);
    }

    if (!drawingStateRef.current) {
      drawingStateRef.current = { tempObjects: [], points: [] };
    }
    const state = drawingStateRef.current;

    // F19: Shift+line snap — on second click, snap to horizontal/vertical axis relative to first point
    let pt = { x: worldPt.x, y: worldPt.y };
    if (shiftHeld && req.type === "line" && state.points.length === 1) {
      pt = snapToAxis(state.points[0], pt);
      worldPt = new Point(pt.x, pt.y);
    }

    state.points.push({ x: worldPt.x, y: worldPt.y });

    const isLine = req.type === "line";
    const color = isLine ? LINE_STROKE : ZONE_STROKE;

    const marker = new Circle({
      left: worldPt.x,
      top: worldPt.y,
      radius: DRAW_VERTEX_RADIUS,
      fill: "#ffffff",
      stroke: color,
      strokeWidth: 1,
      originX: "center",
      originY: "center",
      selectable: false,
      evented: false,
    });
    canvas.add(marker);
    state.tempObjects.push(marker);

    if (isLine) {
      if (state.points.length === 2) {
        const coords = state.points.map((p) =>
          getNormalizedPoint(pixelToPercentStatic(p)),
        );
        clearDrawingTemp(canvas);
        const target = drawingTargetRef.current;
        if (target && target.type === "line") {
          onDrawingTargetCompleteRef.current?.("line", target.id, coords);
        } else {
          onDrawingCompleteRef.current("line", req.eventType, coords);
        }
      } else if (state.points.length === 1) {
        const preview = new Line(
          [worldPt.x, worldPt.y, worldPt.x, worldPt.y],
          {
            stroke: color,
            strokeWidth: 2,
            selectable: false,
            evented: false,
            strokeDashArray: [5, 5],
          },
        );
        canvas.add(preview);
        state.tempObjects.push(preview);
      }
    } else {
      removePreviewLine(canvas, state);

      if (state.points.length >= 4 && isNearFirstPoint(state.points[state.points.length - 1], state, canvas.getZoom())) {
        state.points.pop();
        const coords = state.points.map((p) =>
          getNormalizedPoint(pixelToPercentStatic(p)),
        );
        clearDrawingTemp(canvas);
        const target = drawingTargetRef.current;
        if (target && target.type === "zone") {
          onDrawingTargetCompleteRef.current?.("zone", target.id, coords);
        } else {
          onDrawingCompleteRef.current("zone", req.eventType, coords);
        }
        return;
      }

      if (state.points.length >= 2) {
        const prev = state.points[state.points.length - 2];
        const segLine = new Line([prev.x, prev.y, worldPt.x, worldPt.y], {
          stroke: color,
          strokeWidth: 2,
          selectable: false,
          evented: false,
          strokeDashArray: [5, 5],
        });
        canvas.add(segLine);
        state.tempObjects.push(segLine);
      }

      createPreviewLine(canvas, state, color);
    }

    canvas.requestRenderAll();
  }

  function handleDrawingMove(canvas: Canvas, worldPt: Point, shiftHeld?: boolean) {
    const state = drawingStateRef.current;
    const req = drawingRequestRef.current;
    if (!state || state.points.length === 0 || !req) return;

    // Clamp preview point to image bounds
    const bgObj = getBgImage();
    const ns = imageNaturalSizeRef.current;
    let clampedPt = { x: worldPt.x, y: worldPt.y };
    if (bgObj && ns) {
      clampedPt = clampToImageBounds(clampedPt, bgObj, ns);
    }

    // F19: Shift+line snap — preview snaps to horizontal/vertical axis
    if (shiftHeld && req.type === "line" && state.points.length === 1) {
      clampedPt = snapToAxis(state.points[0], clampedPt);
    }

    if (req.type === "line" && state.points.length === 1) {
      const previewLine = state.tempObjects.find(
        (o) => o instanceof Line,
      ) as Line | undefined;
      if (previewLine) {
        previewLine.set({ x2: clampedPt.x, y2: clampedPt.y });
        canvas.requestRenderAll();
      }
    }

    // Zone: update preview line + highlight first vertex on hover
    if (req.type === "zone") {
      updatePreviewLine(canvas, state, clampedPt);
      updateFirstMarkerOnHover(canvas, state, clampedPt, canvas.getZoom());
    }
  }

  function handleDrawingDblClick(canvas: Canvas) {
    const state = drawingStateRef.current;
    const req = drawingRequestRef.current;
    if (!state || !req || req.type !== "zone") return;

    if (state.points.length > 1) {
      state.points.pop();
    }

    if (state.points.length >= 3) {
      const coords = state.points.map((p) =>
        getNormalizedPoint(pixelToPercentStatic(p)),
      );
      clearDrawingTemp(canvas);
      const target = drawingTargetRef.current;
      if (target && target.type === "zone") {
        onDrawingTargetCompleteRef.current?.("zone", target.id, coords);
      } else {
        onDrawingCompleteRef.current("zone", req.eventType, coords);
      }
    } else {
      clearDrawingTemp(canvas);
    }
  }

  function clearDrawingTemp(canvas: Canvas) {
    const state = drawingStateRef.current;
    if (state) {
      for (const obj of state.tempObjects) {
        canvas.remove(obj);
      }
    }
    drawingStateRef.current = null;
    canvas.requestRenderAll();
  }

  // --- F19: Rectangle zone drawing handlers (inline) ---

  const RECT_MIN_SIZE = 4;

  function handleRectDrawingMouseDown(_canvas: Canvas, worldPt: Point) {
    const req = drawingRequestRef.current;
    if (!req || req.type !== "zone") return;

    const bgObj = getBgImage();
    const ns = imageNaturalSizeRef.current;
    let pt = { x: worldPt.x, y: worldPt.y };
    if (bgObj && ns) pt = clampToImageBounds(pt, bgObj, ns);

    if (!drawingStateRef.current) {
      drawingStateRef.current = { tempObjects: [], points: [] };
    }
    drawingStateRef.current.rectDragStart = pt;
  }

  function handleRectDrawingMouseMove(canvas: Canvas, worldPt: Point) {
    const state = drawingStateRef.current;
    if (!state || !state.rectDragStart) return;

    const bgObj = getBgImage();
    const ns = imageNaturalSizeRef.current;
    let pt = { x: worldPt.x, y: worldPt.y };
    if (bgObj && ns) pt = clampToImageBounds(pt, bgObj, ns);

    const start = state.rectDragStart;
    const left = Math.min(start.x, pt.x);
    const top = Math.min(start.y, pt.y);
    const width = Math.abs(pt.x - start.x);
    const height = Math.abs(pt.y - start.y);

    // Remove existing rect preview
    const oldPreview = state.tempObjects.find((o: any) => o._isRectPreview);
    if (oldPreview) {
      canvas.remove(oldPreview);
      state.tempObjects = state.tempObjects.filter((o) => o !== oldPreview);
    }

    const rect = new Rect({
      left,
      top,
      width,
      height,
      originX: "left",
      originY: "top",
      fill: "rgba(25, 227, 255, 0.12)",
      stroke: ZONE_STROKE,
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
    });
    (rect as any)._isRectPreview = true;
    canvas.add(rect);
    state.tempObjects.push(rect);
    canvas.requestRenderAll();
  }

  function handleRectDrawingMouseUp(canvas: Canvas, worldPt: Point) {
    const state = drawingStateRef.current;
    if (!state || !state.rectDragStart) return;

    const bgObj = getBgImage();
    const ns = imageNaturalSizeRef.current;
    let pt = { x: worldPt.x, y: worldPt.y };
    if (bgObj && ns) pt = clampToImageBounds(pt, bgObj, ns);

    const start = state.rectDragStart;
    const left = Math.min(start.x, pt.x);
    const top = Math.min(start.y, pt.y);
    const right = Math.max(start.x, pt.x);
    const bottom = Math.max(start.y, pt.y);

    state.rectDragStart = null;

    // Ignore too-small rectangles (accidental clicks)
    if (right - left < RECT_MIN_SIZE || bottom - top < RECT_MIN_SIZE) {
      clearDrawingTemp(canvas);
      return;
    }

    const coords = [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ].map((p) => getNormalizedPoint(pixelToPercentStatic(p)));

    clearDrawingTemp(canvas);
    const target = drawingTargetRef.current;
    if (target && target.type === "zone") {
      onDrawingTargetCompleteRef.current?.("zone", target.id, coords);
    } else {
      const req = drawingRequestRef.current;
      if (req) {
        onDrawingCompleteRef.current("zone", req.eventType, coords);
      }
    }
  }

  // --- Background image ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    suppressSelectionClearedRef.current = true;
    canvas.clear();
    suppressSelectionClearedRef.current = false;
    imageNaturalSizeRef.current = null;
    setImageLoaded(false);
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0] as TMat2D);
    setZoom(1);

    if (!snapshotUrl) {
      canvas.requestRenderAll();
      return;
    }

    let cancelled = false;

    FabricImage.fromURL(snapshotUrl).then((img) => {
      if (cancelled || !fabricRef.current) return;

      const naturalW = img.width!;
      const naturalH = img.height!;
      const ns = { width: naturalW, height: naturalH };
      imageNaturalSizeRef.current = ns;

      img.set({
        originX: "center",
        originY: "center",
        selectable: false,
        evented: false,
      });
      (img as any).__isSnapshotBg = true;

      // PO QA 2차 o: 가용 영역(top/bottom padding 제외) 기준 contain fit
      fitBgToCanvas(canvas, img, ns, topPaddingRef.current, bottomPaddingRef.current, verticalAlignRef.current);
      lastFitAlignRef.current = verticalAlignRef.current;
      // PO QA 3차 ab-4: 카메라 진입 시 reference scale 저장 (라벨/화살표 비례 축소 기준)
      refBgScaleRef.current = img.scaleX ?? null;

      canvas.add(img);
      canvas.sendObjectToBack(img);
      canvas.requestRenderAll();
      setBgFitVersion((v) => v + 1);
      setImageLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [snapshotUrl]);

  // B6: verticalAlign 변경(viewMode 전환) 시 배경 리핏.
  // topPadding/bottomPadding 변경(메모 오버레이·카메라 선택 등)에선 기본적으로 이미지 크기 유지하되,
  // QA-final: 사용자가 아직 조작하지 않은 최초 정착 단계(오버레이 측정으로 padding 0→실제값)에서는
  //   padding 변경에도 리핏해 첫 진입부터 올바른 크기로 보이게 한다.
  // 캔버스가 숨겨진 상태(display:none → 0 크기)에서는 리핏 스킵 — ResizeObserver가 visible 전환 시 처리.
  useEffect(() => {
    if (!imageLoaded) return;
    const alignChanged = verticalAlign !== lastFitAlignRef.current;
    // align 그대로 + 이미 사용자가 조작 시작 → padding 변경은 무시 (이미지 크기 유지)
    if (!alignChanged && userInteractedRef.current) return;
    const canvas = fabricRef.current;
    const ns = imageNaturalSizeRef.current;
    if (!canvas || !ns) return;
    // 캔버스가 숨겨진 상태면 스킵 (visible 전환 시 ResizeObserver 가 리핏)
    if (canvas.getWidth() === 0 || canvas.getHeight() === 0) return;
    const bgImg = canvas
      .getObjects()
      .find((o) => (o as any).__isSnapshotBg === true) as FabricImage | undefined;
    if (!bgImg) return;
    const oldLeft = bgImg.left!;
    const oldTop = bgImg.top!;
    const oldScale = bgImg.scaleX!;
    // 모드 전환 시에만 viewport 줌 리셋 → 정해진 크기로 복원
    // (초기 정착 단계의 padding 리핏에서는 아직 줌이 identity 이므로 리셋 불필요)
    if (alignChanged) {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0] as TMat2D);
      setZoom(1);
    }
    fitBgToCanvas(canvas, bgImg, ns, topPadding, bottomPadding, verticalAlign);
    skipRebuildZoneIdsRef.current.clear();
    skipRebuildLineIdsRef.current.clear();
    repositionObjectsAfterBgRefit(canvas, ns, oldLeft, oldTop, oldScale, "__isSnapshotBg");
    lastFitAlignRef.current = verticalAlign;
    canvas.requestRenderAll();
    setBgFitVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topPadding, bottomPadding, verticalAlign, imageLoaded]);

  // --- Flip direction helper ---
  function flipLineDirection(group: Group): boolean {
    const sg = (group as any).__shapeGroup;
    if (!sg) return false;
    const worldPts = getLineGroupEndpoints(group);
    const reversed = [...worldPts].reverse();
    const coords = reversed.map((p) => getNormalizedPoint(pixelToPercentStatic(p)));
    onLineCoordinatesChangeRef.current(sg.id, coords);
    return true;
  }

  // --- Draw shapes ---
  const shapeObjectsRef = useRef<any[]>([]);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !imageNaturalSizeRef.current) return;
    // PO QA 3차 ab-4: 라벨/화살표/stroke 축소 비율. getVisualScale() 이 lazy init + max-update 로
    // 단독 모드 scale 을 ref 로 유지하므로 단독 모드 시각 크기를 기준으로 둘 다 모드에서만 축소됨.
    const visualScale = getVisualScale();
    // Collect objects that should be preserved (just finished coordinate edit)
    const preservedObjects: any[] = [];
    const skipZones = skipRebuildZoneIdsRef.current;
    const skipLines = skipRebuildLineIdsRef.current;

    if (skipZones.size > 0 || skipLines.size > 0) {
      for (const obj of shapeObjectsRef.current) {
        const sg = (obj as any).__shapeGroup;
        if (sg && sg.type === "zone" && skipZones.has(sg.id)) {
          preservedObjects.push(obj);
        } else if (sg && sg.type === "line" && skipLines.has(sg.id)) {
          preservedObjects.push(obj);
        }
      }
    }
    skipZones.clear();
    skipLines.clear();

    suppressSelectionClearedRef.current = true;
    canvas.discardActiveObject();
    for (const obj of shapeObjectsRef.current) {
      if (!preservedObjects.includes(obj)) {
        canvas.remove(obj);
      }
    }
    suppressSelectionClearedRef.current = false;
    shapeObjectsRef.current = [...preservedObjects];

    if (!showLineZone) {
      // Still need to remove preserved objects if hiding
      canvas.discardActiveObject();
      for (const obj of preservedObjects) {
        canvas.remove(obj);
      }
      shapeObjectsRef.current = [];
      canvas.requestRenderAll();
      return;
    }

    // --- Counting Lines ---
    const linesToRender = countingLines.filter(
      (l) => l.analysisConfig && l.analysisConfig.coordinates.length >= 2,
    );
    for (const line of linesToRender) {
      // Skip if this line was just edited and its objects are preserved
      if (preservedObjects.some((o) => (o as any).__shapeGroup?.type === "line" && (o as any).__shapeGroup?.id === line.id)) {
        continue;
      }

      const coords = line.analysisConfig!.coordinates;
      const p0 = percentToPixelStatic(coords[0]);
      const p1 = percentToPixelStatic(coords[1]);

      const shapeGroup: any = {
        type: "line" as const,
        id: line.id,
        editingMode: "size" as "size" | "coordinate",
        eventType: line.analysisConfig!.eventType,
        lineName: line.analysisConfig!.name,
        fabricGroup: null as Group | null,
        coordinatePolyline: null as Polyline | null,
      };

      const lineGroup = createLineGroup(
        p0, p1,
        line.analysisConfig!.eventType,
        line.analysisConfig!.name,
        lineLabelDefs,
        visualScale,
      );

      (lineGroup as any).__shapeGroup = shapeGroup;
      shapeGroup.fabricGroup = lineGroup;

      canvas.add(lineGroup);
      shapeObjectsRef.current.push(lineGroup);
    }

    // --- Interval Zones ---
    const zonesToRender = intervalZones.filter(
      (z) => z.analysisConfig && z.analysisConfig.coordinates.length >= 3,
    );
    for (const zone of zonesToRender) {
      // Skip if this zone was just edited and its objects are preserved
      if (preservedObjects.some((o) => (o as any).__shapeGroup?.type === "zone" && (o as any).__shapeGroup?.id === zone.id)) {
        continue;
      }

      const coords = zone.analysisConfig!.coordinates;
      const pts = coords.map((c) => percentToPixelStatic(c));

      const shapeGroup: any = {
        type: "zone" as const,
        id: zone.id,
        editingMode: "size" as "size" | "coordinate",
        zoneName: zone.analysisConfig!.name,
        polygon: null as Polygon | null,
        coordinateParts: null as any,
      };

      const centroidX = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const centroidY = pts.reduce((s, p) => s + p.y, 0) / pts.length;

      const relPts = pts.map((p) => ({ x: p.x - centroidX, y: p.y - centroidY }));
      // Create polygon first to get pathOffset (bbox center of points),
      // then set left/top = centroid + pathOffset so visual position is exact.
      const poly = new Polygon(relPts, {
          originX: "center",
          originY: "center",
          fill: ZONE_FILL,
          stroke: ZONE_STROKE,
          strokeWidth: clampScaled(2, visualScale, MIN_STROKE_WIDTH),
          strokeUniform: true,
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true,
          borderColor: BORDER_COLOR,
          borderScaleFactor: 2,
          cornerColor: CORNER_COLOR,
          cornerStrokeColor: CORNER_STROKE_COLOR,
          cornerStyle: "rect",
          cornerSize: 8,
          transparentCorners: false,
          lockScalingFlip: true,
        },
      );
      // Position at bbox center so visual rendering matches world coords exactly
      const polyPo = (poly as any).pathOffset as { x: number; y: number };
      poly.set({ left: centroidX + polyPo.x, top: centroidY + polyPo.y });
      poly.setCoords();

      (poly as any).__shapeGroup = shapeGroup;
      shapeGroup.polygon = poly;

      const nameLabel = createLabelGroup(
        zone.analysisConfig!.name,
        centroidX,
        centroidY,
        {
          originX: "center",
          originY: "center",
          ...scaleLabelStyle(ZONE_LABEL_STYLE, visualScale),
        },
      );
      (nameLabel as any).__shapeGroup = shapeGroup;

      // PO QA 3차 r: 구역 bbox 가 라벨보다 작으면 라벨 hide (도면 패턴 이식). 항상 add + visibility 토글 (ab-3 패턴)
      const polyW = poly.width ?? 0;
      const polyH = poly.height ?? 0;
      const lw = nameLabel.width ?? 0;
      const lh = nameLabel.height ?? 0;
      nameLabel.set({ visible: polyW > lw * 1.2 && polyH > lh * 1.2 });

      const objects = [poly, nameLabel];
      for (const obj of objects) {
        canvas.add(obj);
        shapeObjectsRef.current.push(obj);
      }
    }

    // 도형 그리기 후 선택 상태 복원 (카메라 전환 시 이미지 로드 → 도형 그리기 완료 후 선택)
    const currentSelection = selectedEventSourceRef.current;
    if (currentSelection) {
      const target = shapeObjectsRef.current.find((obj) => {
        const group = (obj as any).__shapeGroup;
        return (
          group &&
          group.type === currentSelection.type &&
          group.id === currentSelection.id &&
          obj.selectable
        );
      });
      if (target) {
        canvas.setActiveObject(target);
      } else {
        // 삭제 등으로 대상 도형이 사라진 경우 툴바도 함께 제거
        setMarkerToolbar(null);
      }
    } else {
      // 선택 해제 상태에서 shape rebuild 시 잔존 툴바 제거
      setMarkerToolbar(null);
    }

    canvas.requestRenderAll();
  }, [countingLines, intervalZones, showLineZone, snapshotUrl, cameraId, imageLoaded, bgFitVersion]);

  // --- Memo markers on snapshot ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !imageNaturalSizeRef.current) return;

    // PO QA 3차 ab-4: 메모 마커도 라벨처럼 배경 scale 에 비례 축소.
    // 너무 작아지면 클릭하기 어려우므로 marker 는 라벨보다 보수적 최소(0.55)로 클램프.
    const markerScale = Math.max(0.55, getVisualScale());

    const existingMarkers = canvas
      .getObjects()
      .filter((o): o is MemoMarker => o instanceof MemoMarker);

    const toAdd: MemoMarker[] = [];
    const toRemove: MemoMarker[] = [];

    // Remove markers no longer in memosOnSnapshot
    for (const obj of existingMarkers) {
      if (!memosOnSnapshot.some((m) => m.id === obj.memoId)) {
        toRemove.push(obj);
      }
    }

    let scaleUpdated = false;

    // Add or update markers
    for (const memo of memosOnSnapshot) {
      if (!memo.positionForFloorPlan) continue;
      const px = percentToPixelStatic(memo.positionForFloorPlan);
      const matched = existingMarkers.find((obj) => obj.memoId === memo.id);

      if (!matched) {
        const marker = new MemoMarker({
          memoId: memo.id,
          cameraId: memo.cameraId,
          left: px.x,
          top: px.y,
        });
        marker.set({ scaleX: markerScale, scaleY: markerScale });
        marker.setCoords();
        toAdd.push(marker);
      } else {
        const posChanged =
          Math.abs(matched.left! - px.x) > 0.01 ||
          Math.abs(matched.top! - px.y) > 0.01;
        const scaleChanged = Math.abs((matched.scaleX ?? 1) - markerScale) > 0.001;
        if (posChanged || scaleChanged) {
          matched.set({ left: px.x, top: px.y, scaleX: markerScale, scaleY: markerScale });
          matched.setCoords();
          if (scaleChanged) scaleUpdated = true;
        }
      }
    }

    let changed = false;
    if (toRemove.length > 0) {
      for (const obj of toRemove) canvas.remove(obj);
      changed = true;
    }
    if (toAdd.length > 0) {
      for (const obj of toAdd) canvas.add(obj);
      changed = true;
    }
    if (changed || scaleUpdated) canvas.requestRenderAll();
  }, [memosOnSnapshot, imageLoaded, bgFitVersion]);

  // --- Memo focus sync ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    let changed = false;
    let activeTarget: MemoMarker | null = null;

    for (const obj of canvas.getObjects()) {
      if (obj instanceof MemoMarker) {
        const shouldFocus = obj.memoId === selectedMemoId;
        if (obj.isFocused !== shouldFocus) {
          obj.updateAppearance({ isFocused: shouldFocus });
          changed = true;
        }
        if (shouldFocus) activeTarget = obj;
      }
    }

    if (activeTarget && canvas.getActiveObject() !== activeTarget) {
      canvas.setActiveObject(activeTarget);
      changed = true;
    }
    if (changed) canvas.requestRenderAll();
  }, [selectedMemoId]);

  // --- 메모 마커 toolbar 위치 동기화 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const syncPosition = () => {
      const tb = memoMarkerToolbar;
      const el = memoMarkerToolbarRef.current;
      if (!el || !tb) return;
      const obj = canvas
        .getObjects()
        .find((o): o is MemoMarker => o instanceof MemoMarker && o.memoId === tb.memoId);
      if (!obj) return;
      const vpt = canvas.viewportTransform;
      const centerX = obj.left! * vpt[0] + vpt[4];
      const centerY = obj.top! * vpt[3] + vpt[5];
      const halfW = (obj.getScaledWidth() * vpt[0]) / 2;
      const halfH = (obj.getScaledHeight() * vpt[3]) / 2;
      el.style.left = `${centerX - halfW}px`;
      el.style.top = `${centerY - halfH}px`;
    };

    if (selectedMemoId == null) {
      setMemoMarkerToolbar((prev) => (prev === null ? prev : null));
      return;
    }
    const obj = canvas
      .getObjects()
      .find((o): o is MemoMarker => o instanceof MemoMarker && o.memoId === selectedMemoId);
    if (!obj) {
      setMemoMarkerToolbar((prev) => (prev === null ? prev : null));
      return;
    }
    setMemoMarkerToolbar((prev) =>
      prev && prev.memoId === selectedMemoId ? prev : { memoId: selectedMemoId },
    );
    syncPosition();

    canvas.on("after:render", syncPosition);
    canvas.on("object:moving", syncPosition);
    return () => {
      canvas.off("after:render", syncPosition);
      canvas.off("object:moving", syncPosition);
    };
  }, [selectedMemoId, memosOnSnapshot, memoMarkerToolbar]);

  // --- Memo placing mode cursor ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    if (memoPlacingMode) {
      // MemoMarker 와 동일한 모양의 SVG 커서 — 카메라매핑 FloorPlanCanvas 와 통일
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><rect x='1' y='1' width='22' height='22' rx='4' fill='rgba(255,255,255,0.48)' stroke='%23696F8C' stroke-width='2'/><svg x='5' y='5' width='14' height='14' viewBox='0 0 16 16'><path d='M16 16H0V0H16V16ZM3.2002 10.4004V12H12.8008V10.4004H3.2002ZM3.2002 7.2002V8.7998H12.8008V7.2002H3.2002ZM3.2002 4V5.60059H9.60059V4H3.2002Z' fill='%23696F8C'/></svg></svg>`;
      const url = `data:image/svg+xml;utf8,${svg}`;
      const cursor = `url("${url}") 12 12, crosshair`;
      // 6차 ar: defaultCursor + hoverCursor 둘 다 세팅 — fabric 은 객체 위에서 target.hoverCursor || canvas.hoverCursor 로 재평가하므로 hoverCursor 빠지면 마커/선/구역 위에서 메모 아이콘 사라짐 (drawing cursor 패턴과 일치)
      canvas.defaultCursor = cursor;
      canvas.hoverCursor = cursor;
      canvas.setCursor(cursor);
    } else if (!drawingRequest) {
      // 평소 grab — 도면 캔버스와 통일
      canvas.defaultCursor = "grab";
      canvas.hoverCursor = "move";
    }
  }, [memoPlacingMode, drawingRequest]);

  // --- Programmatic selection from sidebar ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    if (!selectedEventSource) {
      // Don't discard selection during coordinate editing mode
      if (!activeCoordinateSgRef.current) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
      return;
    }

    const target = shapeObjectsRef.current.find((obj) => {
      const group = (obj as any).__shapeGroup;
      return (
        group &&
        group.type === selectedEventSource.type &&
        group.id === selectedEventSource.id &&
        obj.selectable
      );
    });

    if (target) {
      canvas.setActiveObject(target);
      canvas.requestRenderAll();
    } else if (!activeCoordinateSgRef.current) {
      // PO QA 8차: 선택된 소스가 이 캔버스에 도형이 없으면(예: 스냅샷 미배치 구역) 이전 선택을 비움.
      // 안 그러면 직전에 선택돼 있던 엉뚱한 요소(예: 다른 구역)가 그대로 선택된 채 남음.
      // ⚠️ suppress: 여기서 discard 가 selection:cleared → onSelectionChange(null) 로 전파되면
      //    selectedEventSource 가 다시 비워져 양 캔버스가 핑퐁(플리커)함. 전파만 막고 시각적 선택만 해제.
      suppressSelectionClearedRef.current = true;
      canvas.discardActiveObject();
      suppressSelectionClearedRef.current = false;
      canvas.requestRenderAll();
    }
  }, [selectedEventSource]);

  // --- Drawing mode cursor & cleanup ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // drawingRequest (신규 라인/존) + drawingTarget (기존 라인 보강) 둘 다 같은 커서
    const activeType = drawingRequest?.type ?? drawingTarget?.type;
    if (activeType) {
      // F19: zone drawing cursor depends on sub-mode (polygon vs rectangle)
      let cur: string;
      if (activeType === "zone" && zoneSubModeRef.current === "rectangle") {
        cur = ZONE_RECT_DRAWING_CURSOR;
      } else if (activeType === "zone") {
        cur = ZONE_DRAWING_CURSOR;
      } else {
        cur = LINE_DRAWING_CURSOR;
      }
      canvas.defaultCursor = cur;
      canvas.hoverCursor = cur;
      // PO QA 8차 bg+bj (도면과 일관 적용): 그리기 중에는 기존 객체를 hit-test 대상에서 제외해
      // 클릭이 항상 그리기로 가게 함. 그리기 핸들러는 scenePoint 만 쓰므로 무해.
      canvas.skipTargetFind = true;
      canvas.requestRenderAll();
    } else {
      // 그리기 종료 → 기존 객체 선택/편집 복원.
      // 단, 메모 배치 모드에서는 그리기와 동일하게 hit-test 를 꺼서(skipTargetFind)
      // 기존 라인/구역 위를 클릭해도 그 이벤트소스가 선택되지 않게 한다 (추가 동작 중 기존 요소 선택 방지).
      // 메모 배치는 scenePoint 만 쓰므로 target 탐색을 꺼도 무방.
      canvas.skipTargetFind = !!memoPlacingMode;
      if (!memoPlacingMode) {
        // 6차 ar: memo placing 활성 중이면 cursor 리셋 안 함 — 위 memo placing effect 가 cursor 책임짐
        // 평소 grab — 도면 캔버스와 통일
        canvas.defaultCursor = "grab";
        canvas.hoverCursor = "move";
        if (drawingStateRef.current) {
          clearDrawingTemp(canvas);
        }
        canvas.requestRenderAll();
      }
    }
  }, [drawingRequest, drawingTarget, memoPlacingMode]);

  // --- Zoom handlers ---
  const handleZoomIn = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const newZoom = Math.min(MAX_ZOOM, canvas.getZoom() + 0.1);
    const center = canvas.getCenterPoint();
    canvas.zoomToPoint(center, newZoom);
    setZoom(newZoom);
  }, []);

  const handleZoomOut = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const newZoom = Math.max(MIN_ZOOM, canvas.getZoom() - 0.1);
    const center = canvas.getCenterPoint();
    canvas.zoomToPoint(center, newZoom);
    setZoom(newZoom);
  }, []);

  return (
    <Wrapper ref={containerRef}>
      {/* Fabric.js wraps <canvas> in its own div, breaking React's DOM tracking.
          Isolate it so conditional siblings don't trigger insertBefore errors. */}
      <div>
        <canvas ref={canvasRef} />
      </div>
      {!snapshotUrl && <Blocker />}
      {drawingTarget && (
        <DrawingBanner>
          <Trans
            i18nKey="drawingBanner.messageSnapshot"
            ns="analysis-draft"
            values={{ name: drawingTarget.name }}
            components={{
              1: <strong style={{ color: drawingTarget.type === "zone" ? "#19E3FF" : undefined }} />,
            }}
          />
          {drawingTarget.type === "line" && <>{tDraft("drawingBanner.lineHint")}{tDraft("drawingBanner.lineHintShift")}</>}
          {drawingTarget.type === "zone" && tDraft(zoneSubMode === "rectangle" ? "drawingBanner.zoneHintRect" : "drawingBanner.zoneHint")}
          <CancelButton
            onClick={(e) => {
              e.stopPropagation();
              onDrawingTargetCancel?.();
            }}
          >
            {tDraft("drawingBanner.cancel")}
          </CancelButton>
        </DrawingBanner>
      )}
      <ZoomButtons style={{ bottom: zoomButtonsBottomOffset }}>
        <ZoomButton onClick={handleZoomIn}>
          <Icon
            icon="ic_zoom_in"
            width={20}
            fill="none"
            stroke="currentColor"
          />
        </ZoomButton>
        <ZoomButton onClick={handleZoomOut}>
          <Icon
            icon="ic_zoom_out"
            width={20}
            fill="none"
            stroke="currentColor"
          />
        </ZoomButton>
      </ZoomButtons>
      {markerToolbar && (
        <MarkerToolbar ref={markerToolbarRef}>
          {markerToolbar.type === "line" && (
            <Tooltip
              trigger={
                <MarkerToolbarButton
                  type="button"
                  onClick={handleToolbarSwap}
                  aria-label={tDraft("toolbar.swap")}
                >
                  <Icon icon="ic_exchange" width={16} height={16} />
                </MarkerToolbarButton>
              }
              side="top"
              sideOffset={4}
            >
              {tDraft("toolbar.swap")}
            </Tooltip>
          )}
          {/* PO QA 2차 l ①: 이벤트소스 플로팅 툴바의 "메모 추가" 버튼 제거 —
              해당 버튼이 사용한 것은 사실 카메라 메모로, "이벤트소스에 대한 메모"로 오해 유발.
              카메라 메모는 좌측 InlineMemoSection / MemoPanel 에서 진입. */}
          <Tooltip
            trigger={
              <MarkerToolbarButton
                type="button"
                onClick={handleToolbarDelete}
                aria-label={tDraft("toolbar.removeFromCanvas")}
              >
                <IcEraser width={16} height={16} />
              </MarkerToolbarButton>
            }
            side="top"
            sideOffset={4}
          >
            {`${tDraft("toolbar.removeFromCanvas")} (Backspace)`}
          </Tooltip>
        </MarkerToolbar>
      )}

      {memoMarkerToolbar && (
        <MemoMarkerToolbar ref={memoMarkerToolbarRef}>
          <Tooltip
            trigger={
              <MarkerToolbarButton
                type="button"
                onClick={() => {
                  if (!memoMarkerToolbar) return;
                  // 부모가 확인 다이얼로그를 띄움 — 여기서 캔버스 즉시 제거 안 함 (취소 시 회귀).
                  // 확인 후 positionForFloorPlan=null 로 메모가 업데이트되면 sync effect 가 canvas 마커 제거.
                  onDeleteMemoFromSnapshotRef.current?.(memoMarkerToolbar.memoId);
                }}
                aria-label={tDraft("toolbar.delete")}
              >
                <IcEraser width={16} height={16} />
              </MarkerToolbarButton>
            }
            side="top"
            sideOffset={4}
          >
            {`${tDraft("toolbar.delete")} (Backspace)`}
          </Tooltip>
        </MemoMarkerToolbar>
      )}
    </Wrapper>
  );
}


const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Blocker = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fff;
  z-index: 1;
`;

const DrawingBanner = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(36, 41, 70, 0.92);
  color: #fff;
  border-radius: 8px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(17, 17, 31, 0.16);
`;

const CancelButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  color: #fff;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ZoomButtons = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 5;
  gap: 4px;
  width: 32px;
`;

const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: #242946;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;

const MarkerToolbar = styled.div`
  position: absolute;
  z-index: 12;
  transform: translate(0, calc(-100% - 6px));
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(17, 17, 31, 0.12), 0 0 1px rgba(17, 17, 31, 0.16);
  pointer-events: auto;
`;

// 메모 마커 toolbar — 카메라 매핑 페이지와 동일한 위치(좌상단 + 살짝 안쪽)
const MemoMarkerToolbar = styled(MarkerToolbar)`
  transform: translate(calc(-100% + 6px), calc(-100% + 6px));
`;

const MarkerToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${token("colors.text.primary")};
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover:not(:disabled) {
    background: ${token("colors.action.hover")};
  }

  &:disabled {
    color: ${token("colors.text.disabled")};
    cursor: not-allowed;
  }
`;
